// src/pages/SchoolUsersPage.jsx
import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { alpha } from '@mui/material/styles';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Button,
    CircularProgress,
    LinearProgress,
    Snackbar,
    Alert,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination,
    InputAdornment,
    TableSortLabel,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
    ,Checkbox, FormControlLabel, Menu, ListItemText
} from '@mui/material';
import { 
    School as SchoolIcon, 
    CalendarToday,
    ArrowBack,
    Search,
    FilterList,
    Email,
    Edit,
    Delete,
    DirectionsBus,
    
    Mail,
    Add,
    FileUpload,
    GetApp,
    ToggleOn,
    ToggleOff
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthProvider';
import useRegisterPageRefresh from '../hooks/useRegisterPageRefresh';
import api from '../utils/axiosConfig';
import PermissionGuard from '../components/PermissionGuard';
import usePermissions from '../hooks/usePermissions';
import styled from 'styled-components';
import { normalizeKey } from '../utils/stringHelpers';
import BulkFamilyDraftsReview, { BulkPendingSteps, getDraftIssues, getDuplicateRowsByEmail, getExistingChanges, hasNothingToUpdate } from '../components/BulkFamilyDraftsReview';
import { showDuplicateEmailFromError } from '../utils/duplicateEmailHandler';
import tw from 'twin.macro';
import StudentScheduleModal from '../components/modals/StudentScheduleModal';
import BulkStopsEditor from '../components/paradas/BulkStopsEditor';
import RetroactiveApplyModal from '../components/modals/RetroactiveApplyModal';
import UserServiceStatusModal from '../components/UserServiceStatusModal';
import { getScheduleCodesFromSchool, resolveScheduleCodeFromSlot, slotHasScheduleData } from '../utils/scheduleConfig';
import { getCicloEscolarYear } from '../services/cicloEscolarService';

// Opciones de roles disponibles en esta vista: Padre solamente
const roleOptions = [
    { id: 3, name: 'Padre' }
];

// Filtros disponibles al descargar familias (selección múltiple)
const DOWNLOAD_FAMILY_OPTIONS = [
    { value: 'all', label: 'Todas' },
    { value: 'new', label: 'Nuevas' },
    { value: 'ACTIVE', label: 'Activas' },
    { value: 'PAUSED', label: 'Pausadas' },
    { value: 'SUSPENDED', label: 'Suspendidas' },
    { value: 'INACTIVE', label: 'Inactivas' }
];
const DOWNLOAD_FAMILY_OPTION_LABELS = DOWNLOAD_FAMILY_OPTIONS.reduce((acc, o) => { acc[o.value] = o.label; return acc; }, {});

const stripArchivedEmailPrefix = (email) => String(email || '').replace(/^ARCHIVED_ID:?\d+_/i, '');
const getVisibleUserEmail = (user) => stripArchivedEmailPrefix(user?.email || user?.motherEmail || user?.fatherEmail || '');

const PageContainer = styled.div`
    ${tw`bg-gray-50 min-h-screen w-full`}
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;

    @media (max-width: 640px) {
        padding: 1rem;
    }
`;

const HeaderCard = styled(Card)`
    ${tw`mb-6 shadow-lg`}
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
`;

// Familias por petición al guardar la carga masiva (el backend acepta hasta 100 por petición).
const BULK_COMMIT_CHUNK_SIZE = 20;

// Campos de la pantalla de revisión que no forman parte de la familia y no se mandan al servidor.
const toBulkServerDraft = ({ _key, hadIssues, skip, autoSkipped, analyzedCorreo, serverIssue, infoNotice, issues, existing, otherSchool, editing, ...draft }) => draft;

const SchoolUsersPage = () => {
    const { hasPermission, hasAnyPermission } = usePermissions();
    // El mensaje a familia manda correo y/o notificación: basta una de las dos
    // llaves para abrir el diálogo, y cada canal se decide adentro.
    const puedeEnviarMensajeFamilia = hasAnyPermission(['mail-enviar-familia', 'notificaciones-crear']);
    const puedeListarContratos = hasPermission('contratos-listar');
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { cicloEscolarId: routeCicloEscolarId, schoolId } = useParams();

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    // Sorting state
    const [sortBy, setSortBy] = useState(null); // 'status' | 'updatedAt' | 'familyLastName' | 'name' | 'email' | 'role' | 'students'
    const [sortOrder, setSortOrder] = useState('asc');
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [serviceStatusFilter, setServiceStatusFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedUser, setSelectedUser] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const prevFilterKeyRef = useRef('');

    // Nuevos estados para funcionalidades
    
    const [openBulkDialog, setOpenBulkDialog] = useState(false);
    const [openBulkStopsEditor, setOpenBulkStopsEditor] = useState(false);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [openServiceStatusModal, setOpenServiceStatusModal] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openRouteTypeModal, setOpenRouteTypeModal] = useState(false);
    const [openStudentScheduleModal, setOpenStudentScheduleModal] = useState(false);
    const [openFamilyMessageDialog, setOpenFamilyMessageDialog] = useState(false);
    const [familyMessageChannel, setFamilyMessageChannel] = useState('');
    const [familyMessageTitle, setFamilyMessageTitle] = useState('');
    const [familyMessageBody, setFamilyMessageBody] = useState('');
    const [familyMessageFile, setFamilyMessageFile] = useState(null);
    const [familyMessageSending, setFamilyMessageSending] = useState(false);
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

    // Estados para diferentes operaciones
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkDrafts, setBulkDrafts] = useState([]);
    const [bulkAllowedGrades, setBulkAllowedGrades] = useState([]);
    const [bulkAllowedRouteTypes, setBulkAllowedRouteTypes] = useState([]);
    const [bulkPasswordMinLength, setBulkPasswordMinLength] = useState(0);
    const [bulkCicloLabel, setBulkCicloLabel] = useState('');
    const [bulkSaving, setBulkSaving] = useState(false);
    const [bulkSaveProgress, setBulkSaveProgress] = useState({ done: 0, total: 0 });
    // Familias ya guardadas a las que les quedó un paso por completar (pagos, estado de servicio o correo).
    const [bulkPendingFamilies, setBulkPendingFamilies] = useState([]);
    const [bulkCompleting, setBulkCompleting] = useState(false);
    const [bulkConfirmCancelOpen, setBulkConfirmCancelOpen] = useState(false);
    // Dialogo de descarga de familias (selección múltiple de filtros)
    const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
    const [downloadChoice, setDownloadChoice] = useState([]); // ('all' | 'new' | 'ACTIVE' | 'PAUSED' | 'SUSPENDED' | 'INACTIVE')[]
    const [scheduleModalStudents, setScheduleModalStudents] = useState([]);
    const [routeReportLoading, setRouteReportLoading] = useState(false);
    // Actions menu state removed: actions shown inline per row

    // Menu is closed inline where used

    // Estados para formulario de padres
    const [familyDetail, setFamilyDetail] = useState({
        familyLastName: '',
        motherName: '',
        motherCellphone: '',
        motherEmail: '',
        fatherName: '',
        fatherCellphone: '',
        fatherEmail: '',
        razonSocial: '',
        nit: '',
        mainAddress: '',
        alternativeAddress: '',
        zoneOrSector: '',
        routeType: '',
        emergencyContact: '',
        emergencyRelationship: '',
        emergencyPhone: '',
        students: [],
        scheduleSlots: [],
        specialFee: 0
    });
    // Para familias existentes: el tipo de ruta se elige aquí, pero solo se persiste con "Aplicar cambio a pagos"
    const [routeTypeDraft, setRouteTypeDraft] = useState('');
    const [isRouteTypeSelectOpen, setIsRouteTypeSelectOpen] = useState(false);
    const [originalStudents, setOriginalStudents] = useState([]);
    const [newStudent, setNewStudent] = useState({ fullName: '', grade: '' });

    // Estados para Supervisor y Auxiliar
    const [allPilots, setAllPilots] = useState([]);
    const [allMonitoras, setAllMonitoras] = useState([]);
    const [selectedSupervisorPilots, setSelectedSupervisorPilots] = useState([]);
    const [selectedAuxiliarMonitoras, setSelectedAuxiliarMonitoras] = useState([]);

    const getFormattedDateTime = () => {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const hours = String(currentDate.getHours()).padStart(2, '0');
        const minutes = String(currentDate.getMinutes()).padStart(2, '0');
        const seconds = String(currentDate.getSeconds()).padStart(2, '0');
        return `${year}${month}${day}_${hours}${minutes}${seconds}`;
    };

    const translateServiceStatus = (status) => {
        if (!status) return '';
        switch ((status || '').toString()) {
            case 'ACTIVE': return 'Activo';
            case 'PAUSED': return 'Pausado';
            case 'SUSPENDED': return 'Suspendido';
            case 'INACTIVE': return 'Inactivo';
            default: return '';
        }
    };

    // Obtener datos del estado de navegación
    const stateSchool = location.state?.school;
    const stateCicloEscolarId = routeCicloEscolarId || location.state?.cicloEscolarId || stateSchool?.cicloEscolarId || '';
    const stateCicloEscolar = location.state?.cicloEscolar || stateSchool?.cicloEscolar || stateSchool?.CicloEscolar || null;
    const currentCycleLabel = getCicloEscolarYear(stateCicloEscolar);
    const buildSchoolCycleParams = useCallback((targetSchoolId, extra = {}) => ({
        schoolId: targetSchoolId,
        ...(stateCicloEscolarId ? { cicloEscolarId: stateCicloEscolarId } : {}),
        ...extra
    }), [stateCicloEscolarId]);

    // Descargar familias combinando varios filtros en un solo archivo
    const handleDownloadCombinedFamilies = async (selection, schoolIdParam) => {
        const schoolIdToUse = schoolIdParam || schoolId || currentSchool?.id;
        if (!schoolIdToUse) {
            setSnackbar({ open: true, message: 'Por favor selecciona un colegio.', severity: 'warning' });
            return;
        }
        const choices = Array.isArray(selection) ? selection : [selection].filter(Boolean);
        if (choices.length === 0) {
            setSnackbar({ open: true, message: 'Selecciona al menos una opción para descargar.', severity: 'warning' });
            return;
        }

        try {
            setRouteReportLoading(true);

            // Una sola llamada; el backend combina los filtros (unión) y consulta la BD ya filtrada.
            const resp = await api.get('/users/parents/download', {
                params: buildSchoolCycleParams(schoolIdToUse, { filters: choices.join(',') })
            });
            const parents = resp.data.users || [];

            if (parents.length === 0) {
                setSnackbar({ open: true, message: 'No hay familias para descargar con los filtros seleccionados.', severity: 'info' });
                return;
            }

            const optionsSlug = choices
                .map((v) => (DOWNLOAD_FAMILY_OPTION_LABELS[v] || v))
                .join('_')
                .replace(/[^a-zA-Z0-9]+/g, '_')
                .toLowerCase();
            await buildParentsWorkbookAndDownload(parents, schoolIdToUse, `familias_${optionsSlug}`);
        } catch (error) {
            console.error('[handleDownloadCombinedFamilies] Error:', error);
            setSnackbar({ open: true, message: 'Error al generar el archivo de familias', severity: 'error' });
        } finally {
            setRouteReportLoading(false);
        }
    };

    const handleDownloadRouteReport = async (schoolIdParam) => {
        const schoolIdToUse = schoolIdParam || schoolId || currentSchool?.id;
        if (!schoolIdToUse) {
            setSnackbar({ open: true, message: 'Por favor selecciona un colegio.', severity: 'warning' });
            return;
        }

        setRouteReportLoading(true);
        // Derive schedule codes from current school or use defaults
        const SCHEDULE_CODES = getScheduleCodesFromSchool(currentSchool?.schedules);
        try {
            // Usar endpoint del backend que filtra por activos
            const resp = await api.get('/users/parents/download', {
                params: buildSchoolCycleParams(schoolIdToUse, { mode: 'active' })
            });
            const activeParents = resp.data.users || [];

            const parentsWithRoutes = activeParents.filter(u =>
                u.FamilyDetail &&
                (
                    (Array.isArray(u.FamilyDetail.ScheduleSlots) && u.FamilyDetail.ScheduleSlots.length > 0) ||
                    (Array.isArray(u.FamilyDetail.Students) && u.FamilyDetail.Students.some(s => Array.isArray(s.ScheduleSlots) && s.ScheduleSlots.length > 0))
                )
            );

            // Build route summary like original
            const routeSummary = {};
            parentsWithRoutes.forEach(user => {
                const fd = user.FamilyDetail || {};
                if (!fd.Students || fd.Students.length === 0) return;
                fd.Students.forEach(student => {
                    const studentSlots = Array.isArray(student.ScheduleSlots) ? student.ScheduleSlots : [];
                    const familySlots = Array.isArray(fd.ScheduleSlots) ? fd.ScheduleSlots.filter(s => !s.studentId || Number(s.studentId) === Number(student.id)) : [];
                    const slotsToUse = studentSlots.length > 0 ? studentSlots : familySlots;
                    const studentRoutePeriodSet = new Set();
                    slotsToUse.forEach(slot => {
                        let routeNumber = '';
                        if (slot && slot.routeNumber != null && String(slot.routeNumber).trim() !== '') {
                            routeNumber = String(slot.routeNumber);
                        }
                        if (!routeNumber) routeNumber = 'Sin Ruta';
                        const period = resolveScheduleCodeFromSlot(slot, SCHEDULE_CODES);
                        if (!period) return;
                        studentRoutePeriodSet.add(`${routeNumber}::${period}`);
                    });
                    studentRoutePeriodSet.forEach(k => {
                        const [routeNumber, period] = k.split('::');
                        if (!routeSummary[routeNumber]) {
                            routeSummary[routeNumber] = Object.fromEntries(SCHEDULE_CODES.map(c => [`cant${c}`, 0]));
                        }
                        if (period && routeSummary[routeNumber][`cant${period}`] !== undefined) {
                            routeSummary[routeNumber][`cant${period}`]++;
                        }
                    });
                });
            });

            // Determine max students
            let maxStudents = 0;
            parentsWithRoutes.forEach(user => {
                const fd = user.FamilyDetail || {};
                if (!fd.Students || fd.Students.length === 0) return;
                const hasStudentWithRoute = fd.Students.some(student => {
                    const studentSlots = Array.isArray(student.ScheduleSlots) ? student.ScheduleSlots : [];
                    if (studentSlots.length > 0) return studentSlots.some(slotHasScheduleData);
                    if (Array.isArray(fd.ScheduleSlots) && fd.ScheduleSlots.length > 0) return fd.ScheduleSlots.some(slotHasScheduleData);
                    return false;
                });
                if (hasStudentWithRoute && fd.Students.length > maxStudents) maxStudents = fd.Students.length;
            });

            // familiesWithRoutes: filter parents that have at least one student with schedule slots
            const familiesWithRoutes = parentsWithRoutes.filter(user => {
                const fd = user.FamilyDetail || {};
                if (!fd.Students || fd.Students.length === 0) return false;
                if (Array.isArray(fd.ScheduleSlots) && fd.ScheduleSlots.length > 0) {
                    if (fd.ScheduleSlots.some(slotHasScheduleData)) return true;
                }
                return fd.Students.some(student => {
                    const studentSlots = Array.isArray(student.ScheduleSlots) ? student.ScheduleSlots : [];
                    return studentSlots.some(slotHasScheduleData);
                });
            });

            const workbook = new ExcelJS.Workbook();
            // Weekday map and contact columns used by per-day sheets
            const weekdaysMap = [ { key: 'monday', label: 'Lunes' }, { key: 'tuesday', label: 'Martes' }, { key: 'wednesday', label: 'Miércoles' }, { key: 'thursday', label: 'Jueves' }, { key: 'friday', label: 'Viernes' } ];
            const contactCols = ['Estado Servicio', 'Nombre Padre', 'Email Padre', 'Nombre Mamá', 'Teléfono Mamá', 'Nombre Papá', 'Teléfono Papá'];

            // Create a worksheet per weekday with per-student schedule columns for that day
            weekdaysMap.forEach((day) => {
                const sheet = workbook.addWorksheet(day.label);
                const headers = ["Apellido Familia", "Tipo Ruta", "Dirección Principal", "Dirección Alterna"];
                for (let i = 1; i <= maxStudents; i++) {
                    headers.push(`Estudiante ${i} - Nombre`);
                    headers.push(`Estudiante ${i} - Grado`);
                    for (const code of SCHEDULE_CODES) {
                        headers.push(`Estudiante ${i} - Hora ${code}`);
                        headers.push(`Estudiante ${i} - Ruta ${code}`);
                        headers.push(`Estudiante ${i} - Nota Parada ${code}`);
                    }
                }
                headers.push(...contactCols);

                const headerRow = sheet.addRow(headers);
                headerRow.eachCell(cell => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF70AD47' } }; cell.font = { color: { argb: 'FFFFFFFF' }, bold: true }; cell.alignment = { horizontal: 'center', vertical: 'middle' }; cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; });

                familiesWithRoutes.forEach((user, familyIndex) => {
                    const fd = user.FamilyDetail || {};
                    const row = [];
                    row.push(fd.familyLastName || '');
                    row.push(fd.routeType || '');
                    row.push(fd.mainAddress || '');
                    row.push(fd.alternativeAddress || '');
                    for (let i = 0; i < maxStudents; i++) {
                        const student = (fd.Students && fd.Students[i]) ? fd.Students[i] : null;
                        row.push(student ? (student.fullName || '') : '');
                        row.push(student ? (student.grade || '') : '');

                        // prepare day-specific defaults
                        const defaultPer = {};
                        SCHEDULE_CODES.forEach(c => { defaultPer[`hora${c}`] = ''; defaultPer[`ruta${c}`] = ''; defaultPer[`parada${c}`] = ''; });
                        let dataForDay = { ...defaultPer };

                        if (student) {
                            const studentSlots = Array.isArray(student.ScheduleSlots) ? student.ScheduleSlots : [];
                            const familySlots = Array.isArray(fd.ScheduleSlots) ? fd.ScheduleSlots.filter(s => !s.studentId || Number(s.studentId) === Number(student.id)) : [];
                            const slotsToUse = studentSlots.length > 0 ? studentSlots : familySlots;
                            slotsToUse.forEach(slot => {
                                let slotDays = [];
                                if (Array.isArray(slot.days)) slotDays = slot.days;
                                else if (typeof slot.days === 'string') {
                                    try { const parsed = JSON.parse(slot.days); if (Array.isArray(parsed)) slotDays = parsed; else slotDays = [slot.days]; } catch (e) { slotDays = slot.days.split ? slot.days.split(',').map(x => x.trim()) : [slot.days]; }
                                } else if (slot.days) slotDays = [slot.days];
                                const normalizedDays = slotDays.map(d => d ? d.toString().toLowerCase() : '').filter(Boolean);
                                if (!normalizedDays.includes(day.key)) return;
                                const displayTime = (slot.time || slot.schoolSchedule || slot.timeSlot || '').toString();
                                const note = slot.note || '';
                                let routeLabel = '';
                                if (slot && slot.routeNumber != null && String(slot.routeNumber).trim() !== '') routeLabel = String(slot.routeNumber);
                                const period = resolveScheduleCodeFromSlot(slot, SCHEDULE_CODES);
                                if (!period) return;
                                if (!dataForDay[`hora${period}`]) dataForDay[`hora${period}`] = displayTime;
                                if (!dataForDay[`ruta${period}`]) dataForDay[`ruta${period}`] = routeLabel;
                                if (!dataForDay[`parada${period}`]) dataForDay[`parada${period}`] = note;
                            });
                        }

                        // push per-period fields for this day
                        const parseRouteNumber = (r) => {
                            if (r == null) return null;
                            const s = String(r).trim();
                            if (s === '') return null;
                            // Try integer first, then float
                            const n = Number(s);
                            return Number.isFinite(n) ? n : null;
                        };

                        for (const code of SCHEDULE_CODES) {
                            row.push(dataForDay[`hora${code}`]);
                            row.push(parseRouteNumber(dataForDay[`ruta${code}`]));
                            row.push(dataForDay[`parada${code}`]);
                        }
                    }

                    const motherName = (fd.motherName && fd.motherName.trim()) || '';
                    const motherPhone = (fd.motherCellphone && fd.motherCellphone.trim()) || '';
                    const fatherName = (fd.fatherName && fd.fatherName.trim()) || '';
                    const fatherPhone = (fd.fatherCellphone && fd.fatherCellphone.trim()) || '';
                    row.push(translateServiceStatus(user.familyServiceStatus?.status || user.serviceStatus || ''));
                    row.push(user.name || '');
                    row.push(user.email || '');
                    row.push(motherName);
                    row.push(motherPhone);
                    row.push(fatherName);
                    row.push(fatherPhone);

                    const rowObj = sheet.addRow(row);
                    const isEven = (familyIndex + 1) % 2 === 0;
                    rowObj.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFF2F2F2' : 'FFFFFFFF' } }; cell.alignment = { horizontal: 'center', vertical: 'middle' }; cell.border = { top: { style: 'thin', color: { argb: 'FFD0D0D0' } }, left: { style: 'thin', color: { argb: 'FFD0D0D0' } }, bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } }, right: { style: 'thin', color: { argb: 'FFD0D0D0' } } }; });
                });

                // Auto-size per-day sheet columns
                sheet.columns.forEach((column, index) => {
                    const header = headers[index];
                    let maxWidth = header ? header.length : 10;
                    sheet.eachRow((row, rowNumber) => {
                        if (rowNumber > 1) {
                            const cell = row.getCell(index + 1);
                            const cellLength = String(cell.value || "").length;
                            if (cellLength > maxWidth) maxWidth = cellLength;
                        }
                    });
                    column.width = Math.min(Math.max(maxWidth, 10), 50);
                });

                if (sheet.rowCount > 1 && headers.length > 0) {
                    const getColumnLetter = (columnNumber) => {
                        let letter = '';
                        while (columnNumber > 0) {
                            const remainder = (columnNumber - 1) % 26;
                            letter = String.fromCharCode(65 + remainder) + letter;
                            columnNumber = Math.floor((columnNumber - 1) / 26);
                        }
                        return letter;
                    };
                    const maxColumns = Math.min(headers.length, 1024);
                    const lastColumnLetter = getColumnLetter(maxColumns);
                    const filterRange = `A1:${lastColumnLetter}${sheet.rowCount}`;
                    sheet.autoFilter = filterRange;
                    for (let i = 0; i < maxColumns; i++) {
                        const columnLetter = getColumnLetter(i + 1);
                        try { const column = sheet.getColumn(columnLetter); if (column) column.filterButton = true; } catch (error) { break; }
                    }
                }

                sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];
            });

            const selectedSchool = schools.find(s => s.id === parseInt(schoolIdToUse));
            const schoolName = selectedSchool ? selectedSchool.name : 'Colegio';
            const fileName = `reporte_rutas_${schoolName.replace(/[^a-zA-Z0-9]/g, '_')}_${getFormattedDateTime()}.xlsx`;
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setSnackbar({ open: true, message: `Reporte de rutas para ${schoolName} descargado exitosamente`, severity: 'success' });
        } catch (error) {
            console.error('[handleDownloadRouteReport] Error:', error);
            setSnackbar({ open: true, message: 'Error al descargar el reporte de rutas', severity: 'error' });
        } finally {
            setRouteReportLoading(false);
        }
    };

    // Helper: build full parents workbook (same columns as previous implementation) and trigger download
    const buildParentsWorkbookAndDownload = async (parents, schoolIdToUse, prefix) => {
        try {
            // Determinar máximo de hijos entre las familias
            let maxStudents = 0;
            parents.forEach(u => {
                const fd = u.FamilyDetail || {};
                const cnt = Array.isArray(fd.Students) ? fd.Students.length : 0;
                if (cnt > maxStudents) maxStudents = cnt;
            });

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Padres');

            const baseHeaders = [
                { header: 'Apellido Familia', key: 'apellidoFamilia' },
                { header: 'Estado Familia', key: 'estadoFamilia' },
                { header: 'Estado Servicio', key: 'estadoServicio' },
                { header: 'Nombre', key: 'nombre' },
                { header: 'Email', key: 'email' },
                { header: 'Nombre Madre', key: 'madreNombre' },
                { header: 'Celular Madre', key: 'madreCelular' },
                { header: 'Email Madre', key: 'madreEmail' },
                { header: 'Nombre Padre', key: 'padreNombre' },
                { header: 'Celular Padre', key: 'padreCelular' },
                { header: 'Email Padre', key: 'padreEmail' },
                { header: 'Dirección Principal', key: 'direccionPrincipal' },
                { header: 'Dirección Alterna', key: 'direccionAlterna' },
                { header: 'Zona/Sector', key: 'zonaSector' },
                { header: 'Tipo Ruta', key: 'tipoRuta' },
                { header: 'Contacto Emergencia', key: 'emergenciaContacto' },
                { header: 'Parentesco Emergencia', key: 'emergenciaParentesco' },
                { header: 'Teléfono Emergencia', key: 'emergenciaTelefono' }
            ];

            const studentCols = [];
            for (let i = 1; i <= maxStudents; i++) {
                studentCols.push({ header: `Estudiante ${i} - Nombre`, key: `est_${i}_nombre` });
                studentCols.push({ header: `Estudiante ${i} - Grado`, key: `est_${i}_grado` });
            }

            const finalColumns = baseHeaders.concat(studentCols);
            sheet.columns = finalColumns.map(col => ({ header: col.header, key: col.key, width: Math.min(Math.max(col.header.length + 6, 12), 40) }));

            sheet.getRow(1).eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });

            sheet.eachRow((row, rowIndex) => {
                if (rowIndex === 1) return;
                const isEven = rowIndex % 2 === 0;
                row.eachCell((cell) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFF2F2F2' : 'FFFFFFFF' } };
                    cell.alignment = { vertical: 'middle' };
                });
            });

            sheet.columns.forEach((col) => {
                const key = col.key;
                if (key === 'madreCelular' || key === 'padreCelular') {
                    col.alignment = { horizontal: 'center', vertical: 'middle' };
                }
                if (key && key.startsWith('est_') && key.endsWith('_grado')) {
                    col.alignment = { horizontal: 'center', vertical: 'middle' };
                }
            });

            sheet.autoFilter = { from: 'A1', to: `${sheet.getColumn(sheet.getRow(1).cellCount).letter}1` };
            sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];

            parents.forEach((u, idx) => {
                const fd = u.FamilyDetail || {};
                const row = [];
                const statusText = u.status || (u.state === 0 ? 'Inactivo' : 'Activo');
                row.push(fd.familyLastName || '');
                row.push(statusText);
                row.push(translateServiceStatus(u.familyServiceStatus?.status || u.serviceStatus || ''));
                row.push(u.name || '');
                row.push(u.email || '');
                row.push(fd.motherName || '');
                row.push(fd.motherCellphone || '');
                row.push(fd.motherEmail || '');
                row.push(fd.fatherName || '');
                row.push(fd.fatherCellphone || '');
                row.push(fd.fatherEmail || '');
                row.push(fd.mainAddress || '');
                row.push(fd.alternativeAddress || '');
                row.push(fd.zoneOrSector || '');
                row.push(fd.routeType || '');
                row.push(fd.emergencyContact || '');
                row.push(fd.emergencyRelationship || '');
                row.push(fd.emergencyPhone || '');

                for (let i = 0; i < maxStudents; i++) {
                    const student = Array.isArray(fd.Students) && fd.Students[i] ? fd.Students[i] : null;
                    row.push(student ? (student.fullName || '') : '');
                    row.push(student ? (student.grade || '') : '');
                }

                const added = sheet.addRow(row);
                const isEven = (idx + 1) % 2 === 0;
                added.eachCell((cell, colNumber) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFF2F2F2' : 'FFFFFFFF' } };
                    cell.alignment = { vertical: 'middle' };
                    const headerText = (sheet.getRow(1).getCell(colNumber).value || '').toString().toLowerCase();
                    if (headerText.includes('celular') || headerText.includes('tipo ruta') || headerText.includes('grado')) {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const selectedSchool = schools.find(s => s.id === parseInt(schoolIdToUse));
            const schoolName = selectedSchool ? selectedSchool.name : 'Colegio';
            const fileName = `${prefix}_${schoolName.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setSnackbar({ open: true, message: `Archivo con padres de ${schoolName} descargado.`, severity: 'success' });
        } catch (err) {
            console.error('[buildParentsWorkbookAndDownload] Error:', err);
            setSnackbar({ open: true, message: 'Error al generar el archivo de padres', severity: 'error' });
        }
    };

    // (Status computed by backend in download endpoints)

    const fetchAllPilots = async () => {
        if (!hasPermission('usuarios-listar-pilotos')) { setAllPilots([]); return; }
        try {
            const resp = await api.get('/users/pilots');
            setAllPilots(resp.data.users || []);
        } catch (error) {
            console.error('[fetchAllPilots] Error:', error);
            setAllPilots([]);
        }
    };

    const fetchAllMonitoras = async () => {
        if (!hasPermission('usuarios-listar-monitoras')) { setAllMonitoras([]); return; }
        try {
            const resp = await api.get('/users/monitors');
            setAllMonitoras(resp.data.users || []);
        } catch (error) {
            console.error('[fetchAllMonitoras] Error:', error);
            setAllMonitoras([]);
        }
    };

    const handleUserChange = (e) => {
        setSelectedUser(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleFamilyDetailChange = (e) => {
        const { name, value } = e.target;

        // En edición de familia existente: routeType NO debe guardarse con "Guardar cambios"
        if (name === 'routeType' && selectedUser?.id) {
            setRouteTypeDraft(value);

            const nextTrimmed = String(value || '').trim();
            // Abrir siempre el flujo al seleccionar un tipo (incluso si es el mismo),
            // así pueden re-aplicarlo a otros períodos.
            if (nextTrimmed) {
                setOpenRouteTypeModal(true);
            }
            return;
        }

        setFamilyDetail(prev => ({ ...prev, [name]: value }));
    };

    const handleRouteTypePick = (nextValue) => {
        const trimmed = String(nextValue || '').trim();
        if (!selectedUser?.id) return;
        if (!trimmed) return;
        setRouteTypeDraft(nextValue);
        setOpenRouteTypeModal(true);
    };

    const handleCloseRouteTypeModal = () => {
        setOpenRouteTypeModal(false);
        // Si no se aplicó, revertir el select al valor guardado
        setRouteTypeDraft(String(familyDetail.routeType || ''));
    };

    const handleAddStudent = () => {
        if (!newStudent.fullName) return;
        setFamilyDetail(prev => ({
            ...prev,
            students: [...prev.students, newStudent]
        }));
        setNewStudent({ fullName: '', grade: '' });
    };

    const handleToggleSupervisorPilot = useCallback((pilotId) => {
        setSelectedSupervisorPilots(prev => {
            if (prev.includes(pilotId)) {
                return prev.filter(x => x !== pilotId);
            } else {
                return [...prev, pilotId];
            }
        });
    }, []);

    const handleToggleAuxiliarMonitora = useCallback((monitoraId) => {
        setSelectedAuxiliarMonitoras(prev => {
            if (prev.includes(monitoraId)) {
                return prev.filter(id => id !== monitoraId);
            } else {
                return [...prev, monitoraId];
            }
        });
    }, []);
    const [scheduleSchoolId, setScheduleSchoolId] = useState(null);
    // selectedUserForContract removed; use selectedUser when needed for sending contracts
    const [contracts, setContracts] = useState([]);
    const [schools, setSchools] = useState([]);

    // Prefer FamilyDetail-level updatedAt (for the selected school + ciclo) over global User.updatedAt
    const getFamilyUpdatedAt = useCallback((user) => {
        if (!user) return null;
        const fd = user.FamilyDetail || user.familyDetail || null;
        if (!fd) return null;

        const resolveUpdated = (obj) => obj?.updatedAt || obj?.updated_at || obj?.updatedOn || obj?.modifiedAt || obj?.updated || null;

        // If multiple family detail records are present, try to match by schoolId and cicloEscolarId
        if (Array.isArray(fd)) {
            const matched = fd.find(item => {
                if (!item) return false;
                const sid = item.schoolId ?? item.SchoolId ?? item.school?.id ?? item.colegioId ?? null;
                const cid = item.cicloEscolarId ?? item.CicloEscolarId ?? item.cicloId ?? item.cicloEscolar?.id ?? null;
                const schoolMatches = schoolId ? String(sid) === String(schoolId) : true;
                const cycleMatches = stateCicloEscolarId ? String(cid) === String(stateCicloEscolarId) : true;
                return schoolMatches && cycleMatches;
            }) || fd[0];
            return resolveUpdated(matched);
        }

        // If single object, assume it's already the relevant FamilyDetail
        return resolveUpdated(fd);
    }, [schoolId, stateCicloEscolarId]);

    // Prefer FamilyDetail-level createdAt (for the selected school + ciclo)
    const getFamilyCreatedAt = useCallback((user) => {
        if (!user) return null;
        const fd = user.FamilyDetail || user.familyDetail || null;
        if (!fd) return null;

        const resolveCreated = (obj) => obj?.createdAt || obj?.created_at || obj?.createdOn || obj?.created || null;

        if (Array.isArray(fd)) {
            const matched = fd.find(item => {
                if (!item) return false;
                const sid = item.schoolId ?? item.SchoolId ?? item.school?.id ?? item.colegioId ?? null;
                const cid = item.cicloEscolarId ?? item.CicloEscolarId ?? item.cicloId ?? item.cicloEscolar?.id ?? null;
                const schoolMatches = schoolId ? String(sid) === String(schoolId) : true;
                const cycleMatches = stateCicloEscolarId ? String(cid) === String(stateCicloEscolarId) : true;
                return schoolMatches && cycleMatches;
            }) || fd[0];
            return resolveCreated(matched);
        }

        return resolveCreated(fd);
    }, [schoolId, stateCicloEscolarId]);

    const userStatusById = useMemo(() => {
        const familyLastNameCounts = new Map();
        const now = Date.now();

        users.forEach((user) => {
            const lastName = normalizeKey(user.FamilyDetail?.familyLastName || '');
            if (!lastName) return;
            familyLastNameCounts.set(lastName, (familyLastNameCounts.get(lastName) || 0) + 1);
        });

        return new Map(users.map((user) => {
            const familyDetail = user.FamilyDetail;
            if (!familyDetail) return [user.id, '-'];

            // Nuevo: basado en FamilyDetail.createdAt del ciclo seleccionado
            try {
                const famCreated = getFamilyCreatedAt(user);
                if (famCreated) {
                    const createdAtMs = new Date(famCreated).getTime();
                    const diffDays = Number.isFinite(createdAtMs) ? (now - createdAtMs) / (1000 * 60 * 60 * 24) : Infinity;
                    if (diffDays <= 21) return [user.id, 'Nuevo'];
                }
            } catch (e) {
                console.error('Error parsing createdAt for user', user.id, e);
            }

            const lastName = normalizeKey(familyDetail.familyLastName || '');
            if (lastName && (familyLastNameCounts.get(lastName) || 0) > 1) {
                return [user.id, 'Duplicado'];
            }

            // Actualizado: basado en FamilyDetail.updatedAt del ciclo seleccionado (dentro de 21 días y posterior a createdAt)
            try {
                const famUpdated = getFamilyUpdatedAt(user);
                if (famUpdated) {
                    const updatedAtMs = new Date(famUpdated).getTime();
                    const diffDaysUpd = Number.isFinite(updatedAtMs) ? (now - updatedAtMs) / (1000 * 60 * 60 * 24) : Infinity;
                    const famCreated = getFamilyCreatedAt(user);
                    const createdAtMs = famCreated ? new Date(famCreated).getTime() : null;
                    if (diffDaysUpd <= 7 && (!createdAtMs || updatedAtMs > createdAtMs)) return [user.id, 'Actualizado'];
                }
            } catch (e) {
                console.error('Error parsing updatedAt for user', user.id, e);
            }

            return [user.id, '-'];
        }));
    }, [users, getFamilyCreatedAt, getFamilyUpdatedAt]);

    const getUserStatus = useCallback((user) => {
        return userStatusById.get(user?.id) || '-';
    }, [userStatusById]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Nuevo':
                return 'success';
            case 'Duplicado':
                return 'warning';
            case 'Actualizado':
                return 'info';
            case 'Inactivo':
                return 'error';
            default:
                return 'default';
        }
    };

    // Helpers to normalize and count students reliably
    const getStudentsArray = (user) => {
        try {
            const fd = user?.FamilyDetail || user?.familyDetail || {};
            let students = fd?.Students ?? fd?.students ?? [];
            if (!Array.isArray(students)) {
                try {
                    students = JSON.parse(students || '[]');
                } catch (e) {
                    console.error('Error parsing students for user', user.id, e);
                    students = [];
                }
            }
            if (!Array.isArray(students)) students = [];
            // Filter out falsy entries
            return students.filter(s => s && (typeof s === 'object' || String(s).trim() !== ''));
        } catch (e) {
            console.error('Error getting students array for user', user.id, e);
            return [];
        }
    };

    const getStudentsCount = (user) => {
        if (!user) return 0;
        const arr = getStudentsArray(user);
        if (Array.isArray(arr) && arr.length > 0) return arr.length;
        if (typeof user.studentsCount === 'number') return user.studentsCount;
        if (user.studentsCount && !isNaN(Number(user.studentsCount))) return Number(user.studentsCount);
        return 0;
    };

    // Determine bus assignment state for a family's students:
    // - 'all'   : all students have at least one schedule/slot assigned
    // - 'some'  : at least one student has an assignment, at least one doesn't
    // - 'none'  : no students have assignments (or no students)
    const getBusAssignStatus = (user) => {
        try {
            const fd = user?.FamilyDetail || user?.familyDetail || {};
            // If the family itself has schedule slots, consider everyone assigned
            const familySlots = fd?.ScheduleSlots || fd?.scheduleSlots;
            if (Array.isArray(familySlots) && familySlots.length > 0) return 'all';

            const students = getStudentsArray(user);
            const total = Array.isArray(students) ? students.length : 0;
            if (total === 0) return 'none';

            const assigned = students.filter(s => {
                // try several common property names
                let slots = s?.ScheduleSlots || s?.scheduleSlots || s?.ScheduleSlot || s?.scheduleSlot || s?.slots;
                if (!slots && typeof s === 'string') {
                    try { slots = JSON.parse(s); } catch (e) { console.error('Error parsing slots for student', s, e); slots = null; }
                }
                if (typeof slots === 'string') {
                    try { slots = JSON.parse(slots); } catch (e) { console.error('Error parsing slots for student', s, e); slots = null; }
                }
                return Array.isArray(slots) && slots.length > 0;
            }).length;

            if (assigned === 0) return 'none';
            if (assigned === total) return 'all';
            return 'some';
        } catch (e) {
            console.error('Error determining bus assignment status for user', user.id, e);
            return 'none';
        }
    };

        const countStudents = (list) => (Array.isArray(list) ? list.reduce((acc, u) => acc + getStudentsCount(u), 0) : 0);

    // Cargar datos adicionales
    const fetchContracts = useCallback(async () => {
        if (!puedeListarContratos) { setContracts([]); return; }
        try {
            const response = await api.get('/contracts', {
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            setContracts(response.data || []);
        } catch (err) {
            console.error('Error fetching contracts:', err);
            setContracts([]); // Asegurar que siempre sea un array
        }
    }, [auth.token, puedeListarContratos]);

    const fetchSchools = useCallback(async () => {
        try {
            const response = await api.get('/schools', {
                params: { includeArchived: true },
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            // Usar la misma estructura que RolesManagementPage
            setSchools(response.data.schools || response.data || []);
        } catch (err) {
            console.error('Error fetching schools:', err);
            setSchools([]); // Asegurar que siempre sea un array
        }
    }, [auth.token]);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch only parents from the backend endpoint
            const response = await api.get('/users/parents', {
                headers: {
                    Authorization: `Bearer ${auth.token}`,
                },
                params: buildSchoolCycleParams(schoolId)
            });

            const usersData = response.data.users || [];
            // Normalize users: ensure FamilyDetail and Students array exist and compute studentsCount
            const normalized = (usersData || []).map(u => {
                const fdRaw = u.FamilyDetail || u.familyDetail || {};
                let students = fdRaw?.Students ?? fdRaw?.students ?? [];
                if (!Array.isArray(students)) {
                    try { students = JSON.parse(students || '[]'); } catch (e) { students = []; }
                }
                if (!Array.isArray(students)) students = [];
                students = students.filter(s => s && (typeof s === 'object' || String(s).trim() !== ''));
                const fdClean = { ...fdRaw, Students: students };
                const studentsCount = students.length || (u.studentsCount ? Number(u.studentsCount) : 0);
                return { ...u, FamilyDetail: fdClean, studentsCount };
            });
            setUsers(normalized);
            setFilteredUsers(normalized);
        } catch (err) {
            console.error('Error fetching users:', err);
            setSnackbar({ 
                open: true, 
                message: 'Error al obtener los usuarios', 
                severity: 'error' 
            });
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoading(false);
        }
    }, [auth.token, schoolId, buildSchoolCycleParams]);

    useEffect(() => {
        if (auth.token && schoolId) {
            fetchUsers();
            fetchContracts();
            fetchSchools();
            fetchAllPilots();
            fetchAllMonitoras();
        }
    }, [auth.token, schoolId, fetchUsers, fetchContracts, fetchSchools]);

    const refreshPageData = useCallback(async () => {
        if (!auth.token || !schoolId) return;
        await Promise.all([
            fetchUsers(),
            fetchContracts(),
            fetchSchools(),
            fetchAllPilots(),
            fetchAllMonitoras(),
        ]);
    }, [auth.token, schoolId, fetchUsers, fetchContracts, fetchSchools]);

    useRegisterPageRefresh(refreshPageData, [auth.token, schoolId, refreshPageData]);

    useEffect(() => {
        let filtered = users;

        // Filtrar por búsqueda (case-insensitive y sin acentos). Soporta búsqueda por varias palabras.
        if (searchQuery) {
            const normalize = (str) => {
                if (!str) return '';
                try {
                    return str
                        .toString()
                        .normalize('NFD')
                        .replace(/\p{Diacritic}/gu, '')
                        .toLowerCase();
                } catch (e) {
                    // Fallback for environments without Unicode property escapes
                    return str
                        .toString()
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .toLowerCase();
                }
            };

            const tokens = normalize(searchQuery).split(/\s+/).filter(Boolean);

            filtered = filtered.filter(user => {
                const familyLast = normalize(user.FamilyDetail?.familyLastName || user.familyLastName || '');
                const motherName = normalize(user.motherName || user.FamilyDetail?.motherName || '');
                const fatherName = normalize(user.fatherName || user.FamilyDetail?.fatherName || '');
                const motherEmail = normalize(user.motherEmail || '');
                const fatherEmail = normalize(user.fatherEmail || '');
                const name = normalize(user.name || '');
                const email = normalize(stripArchivedEmailPrefix(user.email || ''));

                // For each token, ensure it matches at least one field
                return tokens.every(token => (
                    familyLast.includes(token) ||
                    motherName.includes(token) ||
                    fatherName.includes(token) ||
                    motherEmail.includes(token) ||
                    fatherEmail.includes(token) ||
                    name.includes(token) ||
                    email.includes(token)
                ));
            });
        }

        // Filtrar por estado
        if (statusFilter) {
            if (statusFilter === 'Sin asignaciones') {
                // Show families that do NOT have all their students assigned (partial or none)
                filtered = filtered.filter(user => getBusAssignStatus(user) !== 'all');
            } else {
                filtered = filtered.filter(user => {
                    const status = getUserStatus(user);
                    return status === statusFilter;
                });
            }
        }

        // Filtrar por estado del servicio
        if (serviceStatusFilter) {
            filtered = filtered.filter(user => {
                const userServiceStatus = user.familyServiceStatus?.status;
                return userServiceStatus === serviceStatusFilter;
            });
        }

        setFilteredUsers(filtered);
        const currentFilterKey = `${searchQuery}||${statusFilter}||${serviceStatusFilter}`;
        if (prevFilterKeyRef.current !== currentFilterKey) {
            setPage(0); // Reset page when filters change
            prevFilterKeyRef.current = currentFilterKey;
        }
    }, [users, searchQuery, statusFilter, serviceStatusFilter]);

    const handleBackToDashboard = () => {
        navigate(`/admin/escuelas/ciclo/${stateCicloEscolarId}/${schoolId}`, {
            state: {
                cicloEscolarId: stateCicloEscolarId,
                cicloEscolar: stateCicloEscolar,
                school: currentSchool
            }
        });
    };

    

    const handleSortChange = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
        setPage(0);
    };

    const handleSearch = () => {
        setSearchQuery(searchInput);
        setPage(0);
    };

    const handleClearFilters = () => {
        setSearchInput('');
        setSearchQuery('');
        setStatusFilter('');
        setPage(0);
    };

    // Restore action handlers for table actions
    const handleEditClick = async (user) => {
        const parsedRoleId = Number(user.roleId);
        setSelectedUser({
            ...user,
            roleId: parsedRoleId,
            password: ''
        });

        if (parsedRoleId === 3 && user.FamilyDetail) {
            setFamilyDetail({
                familyLastName: user.FamilyDetail.familyLastName || '',
                motherName: user.FamilyDetail.motherName || '',
                motherCellphone: user.FamilyDetail.motherCellphone || '',
                motherEmail: user.FamilyDetail.motherEmail || '',
                fatherName: user.FamilyDetail.fatherName || '',
                fatherCellphone: user.FamilyDetail.fatherCellphone || '',
                fatherEmail: user.FamilyDetail.fatherEmail || '',
                razonSocial: user.FamilyDetail.razonSocial || '',
                nit: user.FamilyDetail.nit || '',
                mainAddress: user.FamilyDetail.mainAddress || '',
                alternativeAddress: user.FamilyDetail.alternativeAddress || '',
                zoneOrSector: user.FamilyDetail.zoneOrSector || '',
                routeType: user.FamilyDetail.routeType || '',
                emergencyContact: user.FamilyDetail.emergencyContact || '',
                emergencyRelationship: user.FamilyDetail.emergencyRelationship || '',
                emergencyPhone: user.FamilyDetail.emergencyPhone || '',
                students: user.FamilyDetail.Students || [],
                scheduleSlots: user.FamilyDetail.ScheduleSlots || [],
                specialFee: user.FamilyDetail.specialFee ?? 0
            });
            setRouteTypeDraft(user.FamilyDetail.routeType || '');
            setOriginalStudents(user.FamilyDetail.Students || []);
        } else {
            setFamilyDetail({
                familyLastName: '',
                motherName: '',
                motherCellphone: '',
                motherEmail: '',
                fatherName: '',
                fatherCellphone: '',
                fatherEmail: '',
                razonSocial: '',
                nit: '',
                mainAddress: '',
                alternativeAddress: '',
                zoneOrSector: '',
                routeType: '',
                emergencyContact: '',
                emergencyRelationship: '',
                emergencyPhone: '',
                students: [],
                scheduleSlots: [],
                specialFee: 0
            });
            setRouteTypeDraft('');
            setOriginalStudents([]);
        }

        setSelectedSupervisorPilots([]);
        if (parsedRoleId === 6 || (user.Role && user.Role.name === 'Supervisor')) {
            const newArray = user.supervisorPilots ? user.supervisorPilots.map(sp => Number(sp.pilotId)) : [];
            setSelectedSupervisorPilots(newArray);
        }

        if (user.Role?.name === 'Auxiliar') {
            const auxMonitoras = [];
            try {
                const auxMonitorasResp = await api.get(`/users/${user.id}/assigned-monitoras`);
                if (auxMonitorasResp.data && auxMonitorasResp.data.monitoraIds) {
                    auxMonitoras.push(...auxMonitorasResp.data.monitoraIds.map(id => Number(id)));
                }
            } catch (error) {
                console.error('Error al obtener monitoras asignadas al auxiliar:', error);
            }
            setSelectedAuxiliarMonitoras(auxMonitoras);
        } else {
            setSelectedAuxiliarMonitoras([]);
        }

        setOpenEditDialog(true);
    };

    // Activation handlers (moved out so they are accessible from the table)
    const handleActivateClick = (user) => {
        if (!user) return;
        setSelectedUser(user);
        setOpenServiceStatusModal(true);
    };

    const handleSuspendClick = (user) => {
        if (!user) return;
        setSelectedUser(user);
        setOpenServiceStatusModal(true);
    };

    const handleServiceStatusSuccess = () => {
        setSnackbar({ open: true, message: 'Estado del servicio actualizado correctamente', severity: 'success' });
        setSelectedUser(null);
        fetchUsers();
    };

    const handleDeleteClick = (userOrId) => {
        // Accept either user object or id
        if (!userOrId) return;
        if (typeof userOrId === 'object') {
                setSelectedUser(userOrId);
        } else {
            const found = filteredUsers.find(u => u.id === userOrId) || users.find(u => u.id === userOrId);
            setSelectedUser(found ? found : { id: userOrId });
        }
        // Open confirmation dialog after selection
        setOpenDeleteConfirm(true);
    };

    const handleCancelDelete = () => {
        setSelectedUser(null);
        setOpenDeleteConfirm(false);
    };

    const handleConfirmDelete = async () => {
        if (!selectedUser || !selectedUser.id) return;
        const schoolIdToUse = currentSchool?.id || schoolId || stateSchool?.id;
        if (!schoolIdToUse || !stateCicloEscolarId) {
            setSnackbar({ open: true, message: 'No se pudo resolver el colegio y ciclo escolar para eliminar al usuario.', severity: 'error' });
            return;
        }

        try {
            setBulkLoading(true);
            const response = await api.delete(`/users/${selectedUser.id}/school-cycle`, {
                data: {
                    schoolId: schoolIdToUse,
                    cicloEscolarId: stateCicloEscolarId,
                    reason: 'Usuario eliminado desde gestión de familias por ciclo escolar'
                }
            });
            setSnackbar({
                open: true,
                message: response?.data?.message || 'Usuario eliminado del ciclo escolar',
                severity: 'success'
            });
            setOpenDeleteConfirm(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (err) {
            console.error('Error deleting user:', err);
            const message = err?.response?.data?.message || 'Error eliminando el usuario';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setBulkLoading(false);
        }
    };

    const handleAssignBuses = (user) => {
        setSelectedUser(user);
        setScheduleModalStudents(user.FamilyDetail?.Students || []);
        // Ensure the modal receives a schoolId so it can load schedules and route numbers.
        // Prefer the user's assigned school, then the route param, then the currentSchool from location state.
        const sch = user?.schoolId || user?.school || schoolId || currentSchool?.id || null;
        setScheduleSchoolId(sch ? Number(sch) : null);
        setOpenStudentScheduleModal(true);
    };

    const resetFamilyMessageDialog = useCallback(() => {
        setFamilyMessageChannel('');
        setFamilyMessageTitle('');
        setFamilyMessageBody('');
        setFamilyMessageFile(null);
        setFamilyMessageSending(false);
    }, []);

    const handleSendCommunication = (user) => {
        setSelectedUser(user);
        resetFamilyMessageDialog();
        setOpenFamilyMessageDialog(true);
    };

    const handleCloseFamilyMessageDialog = () => {
        setOpenFamilyMessageDialog(false);
        resetFamilyMessageDialog();
    };

    const handleSendFamilyMessage = async () => {
        if (!selectedUser?.id) {
            setSnackbar({ open: true, message: 'No se pudo identificar la familia destino.', severity: 'error' });
            return;
        }

        if (!familyMessageChannel) {
            setSnackbar({ open: true, message: 'Selecciona si quieres enviar correo o notificación push.', severity: 'warning' });
            return;
        }

        if (!familyMessageTitle.trim()) {
            setSnackbar({ open: true, message: 'El título es requerido.', severity: 'warning' });
            return;
        }

        if (!familyMessageBody.trim()) {
            setSnackbar({ open: true, message: 'El cuerpo del mensaje es requerido.', severity: 'warning' });
            return;
        }

        try {
            setFamilyMessageSending(true);

            if (familyMessageChannel === 'email') {
                const formData = new FormData();
                formData.append('userId', String(selectedUser.id));
                formData.append('subject', familyMessageTitle.trim());
                formData.append('body', familyMessageBody.trim());
                formData.append('useSmtp', 'true');
                if (familyMessageFile) {
                    formData.append('file', familyMessageFile);
                }

                await api.post('/mail/send-direct-family', formData, {
                    headers: {
                        Authorization: `Bearer ${auth.token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                setSnackbar({ open: true, message: 'Correo enviado a la familia.', severity: 'success' });
            } else {
                await api.post('/notifications', {
                    type: 'manual',
                    sendPush: true,
                    title: familyMessageTitle.trim(),
                    message: familyMessageBody.trim(),
                    cicloEscolarId: stateCicloEscolarId ? Number(stateCicloEscolarId) : null,
                    metadata: {
                        source: 'school-users-direct-family',
                        channel: 'push',
                        targetUserId: Number(selectedUser.id),
                        schoolId: (selectedUser.schoolId || selectedUser.school || schoolId || currentSchool?.id) ? Number(selectedUser.schoolId || selectedUser.school || schoolId || currentSchool?.id) : null,
                        cicloEscolarId: stateCicloEscolarId ? Number(stateCicloEscolarId) : null,
                    },
                    targetingCriteria: {
                        userIds: [Number(selectedUser.id)],
                        ...(stateCicloEscolarId ? { cicloEscolarId: Number(stateCicloEscolarId) } : {})
                    }
                }, {
                    headers: {
                        Authorization: `Bearer ${auth.token}`,
                    }
                });

                setSnackbar({ open: true, message: 'Notificación push enviada a la familia.', severity: 'success' });
            }

            handleCloseFamilyMessageDialog();
        } catch (err) {
            console.error('[handleSendFamilyMessage] Error:', err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || 'No se pudo enviar la comunicación.',
                severity: 'error'
            });
        } finally {
            setFamilyMessageSending(false);
        }
    };

    const handleSaveUser = async () => {
        try {
            // === Normaliza alumnos CONSERVANDO el id para evitar borrar rutas al editar ===
            const normalizeStudents = (arr = []) =>
                (arr || [])
                    .map(s => ({
                        id: (s?.id ?? null),                         // <<-- clave para que el backend haga match y no borre slots
                        fullName: (s?.fullName ?? '').trim(),
                        grade: (s?.grade ?? '').trim()
                    }))
                    // Orden estable: primero por id (si existe), luego por nombre y grado
                    .sort((a, b) => {
                        const ai = a.id ?? Number.POSITIVE_INFINITY;
                        const bi = b.id ?? Number.POSITIVE_INFINITY;
                        if (ai !== bi) return ai - bi;
                        const byName = a.fullName.localeCompare(b.fullName);
                        if (byName !== 0) return byName;
                        return a.grade.localeCompare(b.grade);
                    });

            // Construye el payload de FamilyDetail SIN schedules (este modal no los edita)
            const familyDetailPayload = { ...familyDetail };
            delete familyDetailPayload.scheduleSlots;
            delete familyDetailPayload.ScheduleSlots; // por compatibilidad

            // IMPORTANTE: En edición, el tipo de ruta NO se actualiza desde este modal.
            // El único flujo válido es el de cambio de tipo de ruta.
            if (selectedUser?.id) {
                delete familyDetailPayload.routeType;
            }

            // Solo envía students si realmente hubo cambios
            const currentStudentsNorm  = normalizeStudents(familyDetail?.students);
            const originalStudentsNorm = normalizeStudents(originalStudents);

            if (
                JSON.stringify(currentStudentsNorm) === JSON.stringify(originalStudentsNorm)
            ) {
                // No hubo cambios de alumnos: no enviar
                delete familyDetailPayload.students;
                delete familyDetailPayload.Students; // por compatibilidad
            } else {
                // Hubo cambios: enviar con id para preservar rutas
                familyDetailPayload.students = currentStudentsNorm;
                delete familyDetailPayload.Students; // por compatibilidad
            }

            const roleIdNum = Number(selectedUser.roleId);

            // Una familia (rol Padre) no puede quedar sin estudiantes: al crear,
            // o al editar enviando la lista de alumnos, debe haber al menos uno
            // con nombre y grado.
            if (roleIdNum === 3) {
                const validStudentsCount = currentStudentsNorm.filter(
                    s => String(s.fullName || '').trim() !== '' && String(s.grade || '').trim() !== ''
                ).length;
                const sendingStudents = Object.hasOwn(familyDetailPayload, 'students');
                if ((!selectedUser.id || sendingStudents) && validStudentsCount === 0) {
                    setSnackbar({ open: true, message: 'La familia debe tener al menos un estudiante con nombre y grado.', severity: 'warning' });
                    return;
                }
                if (!selectedUser.id && !familyDetail.routeType) {
                    setSnackbar({ open: true, message: 'Selecciona el tipo de ruta de la familia.', severity: 'warning' });
                    return;
                }
            }

            // Arma el payload del usuario
            let payload = {
                id: selectedUser.id,
                name: selectedUser.name,
                email: selectedUser.email,
                roleId: roleIdNum,
                // Colegio de la edición: las familias lo traen como schoolId (memberships), el staff como school
                school: (selectedUser.schoolId || selectedUser.school) ? Number(selectedUser.schoolId || selectedUser.school) : null
            };

            if (selectedUser.password && selectedUser.password.trim() !== '') {
                payload.password = selectedUser.password;
            }

            // Evita pisar phoneNumber si no se está editando
            if (typeof selectedUser.phoneNumber !== 'undefined') {
                payload.phoneNumber = selectedUser.phoneNumber;
            }

            // Solo incluye familyDetail si es Padre
            if (roleIdNum === 3) {
                payload.familyDetail = familyDetailPayload;
            }

            // Supervisor: pilotos a cargo
            if (roleIdNum === 6) {
                payload.supervisorPilots = selectedSupervisorPilots;
            }

            // Auxiliar: monitoras asignadas (comparar con roleId numérico del payload)
            if (roleIdNum === 7) {
                payload.monitorasAsignadas = selectedAuxiliarMonitoras;
            }

            // Crear o actualizar (no envíes id en POST)
            if (selectedUser.id) {
                await api.put(`/users/${selectedUser.id}`, payload);
                setSnackbar({ open: true, message: 'Usuario actualizado exitosamente', severity: 'success' });
            } else {
                const { id, ...payloadForPost } = payload;
                await api.post('/users', payloadForPost);
                setSnackbar({ open: true, message: 'Usuario creado exitosamente', severity: 'success' });
            }

            // Refresca y cierra
            fetchUsers();
            setOpenEditDialog(false);
        } catch (err) {
            // Centralized duplicate-email handler: it shows the snackbar and returns true if handled
            try {
                if (showDuplicateEmailFromError(err, setSnackbar)) return;
            } catch (e) {
                // fallthrough to generic handling
            }

            console.error('[handleSaveUser] Error:', err);
            const resp = err?.response?.data || {};
            const messageToShow = resp.message || 'Error al guardar usuario';
            setSnackbar({ open: true, message: messageToShow, severity: 'error' });
        }
    };

    const handleAddUser = () => {
        if (!canCreateUsersInManagedSchool) {
            setSnackbar({ open: true, message: newUserCreationDisabledMessage, severity: 'warning' });
            return;
        }

        setSelectedUser({
            id: null,
            name: '',
            email: '',
            password: '',
            // Auto-assign Padre role and current school when creating a new user
            roleId: 3,
            school: schoolId || currentSchool?.id || ''
        });
        setFamilyDetail({
            familyLastName: '',
            motherName: '',
            motherCellphone: '',
            motherEmail: '',
            fatherName: '',
            fatherCellphone: '',
            fatherEmail: '',
            razonSocial: '',
            nit: '',
            mainAddress: '',
            alternativeAddress: '',
            zoneOrSector: '',
            routeType: '',
            emergencyContact: '',
            emergencyRelationship: '',
            emergencyPhone: '',
            students: [],
            scheduleSlots: [],
            specialFee: 0
        });
        setRouteTypeDraft('');
        setOriginalStudents([]);
        setSelectedSupervisorPilots([]);
        setSelectedAuxiliarMonitoras([]);
        setOpenEditDialog(true);
    };

    // Descargar plantilla sólo para padres. Si hay un colegio seleccionado, prefill del campo Colegio (ID)
    const handleDownloadParentsTemplate = () => {

        const headers = [
            "Apellido Familia",
            "Nombre Completo (Usuario)",
            "Correo electrónico (Usuario)",
            "Contraseña",
            "Nombre de la Madre",
            "Celular de la Madre",
            "Correo de la Madre",
            "Nombre del Padre",
            "Celular del Padre",
            "Correo del Padre",
            "Razón social",
            "NIT",
            "Dirección Principal",
            "Dirección Alterna",
            "Zona/Sector",
            "Contacto de Emergencia",
            "Parentesco de Emergencia",
            "Teléfono de Emergencia",
            "Descuento especial (monto)",
            "Tipo ruta",
            "Alumno 1",
            "Grado Alumno 1",
            "Alumno 2",
            "Grado Alumno 2",
            "Alumno 3",
            "Grado Alumno 3",
            "Alumno 4",
            "Grado Alumno 4"
        ];

        const example = [
            "López Ruiz",
            "UsuarioEjemplo",
            "usuario@ejemplo.com",
            "contraseña123",
            "María López",
            "55512345",
            "maria@email.com",
            "Carlos Pérez",
            "55567890",
            "carlos@email.com",
            "Razón Social Ejemplo",
            "1234567-8",
            "Calle Principal 123",
            "Avenida Secundaria 456",
            "Zona 10",
            "Ana García",
            "Hermana",
            "55598765",
            "0",
            "Completa",
            "Alumno Ejemplo 1",
            "Primero Básico",
            "Alumno Ejemplo 2",
            "Segundo",
            "Alumno Ejemplo 3",
            "Tercero",
            "Alumno Ejemplo 4",
            "Cuarto"
        ];

        // Hoja de listas: únicamente lista de grados del colegio gestionado (una columna)
        // Normalizar grades del colegio gestionado como en availableGrades
        const getManagedSchoolGrades = () => {
            let sch = currentSchool;
            if (!sch && schoolId && Array.isArray(schools)) sch = schools.find(s => String(s.id) === String(schoolId));
            if (!sch && Array.isArray(schools) && schools.length > 0) sch = schools[0];
            const gradesRaw = sch?.grades;
            if (!gradesRaw) return [];
            let parsed = [];
            if (typeof gradesRaw === 'string') {
                try { parsed = JSON.parse(gradesRaw); } catch (e) { parsed = gradesRaw.split(',').map(x => x.trim()).filter(Boolean).map(name => ({ name })); }
            } else if (Array.isArray(gradesRaw)) parsed = gradesRaw; else return [];
            return parsed.map(g => {
                if (!g) return '';
                if (typeof g === 'string') return g;
                if (typeof g === 'object') return g.name ?? g.label ?? JSON.stringify(g);
                return String(g);
            }).filter(Boolean);
        };

        const managedGrades = getManagedSchoolGrades();
        const wsListasData = [["Grados"]];
        if (managedGrades.length === 0) {
            // fallback: include a few common grades to avoid empty list
            wsListasData.push(["Primero Básico"]);
            wsListasData.push(["Segundo"]);
            wsListasData.push(["Tercero"]);
        } else {
            managedGrades.forEach(g => wsListasData.push([g]));
        }

        const wsPadres = XLSX.utils.aoa_to_sheet([headers, example]);
        wsPadres['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 2, 15) }));
        const wsListas = XLSX.utils.aoa_to_sheet(wsListasData);
        wsListas['!cols'] = [{ wch: 30 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsPadres, 'Padres');
        XLSX.utils.book_append_sheet(wb, wsListas, 'Listas');

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const fileName = `plantilla_padres_${getFormattedDateTime()}.xlsx`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    

    const bulkHasDrafts = bulkDrafts.length > 0;
    const bulkIncludedDrafts = useMemo(() => bulkDrafts.filter(draft => !draft.skip), [bulkDrafts]);
    // Se recalcula solo cuando cambian las familias: validar todas en cada render es caro con cargas grandes.
    const bulkUnresolvedCount = useMemo(() => {
        const duplicateRowsByEmail = getDuplicateRowsByEmail(bulkDrafts);
        return bulkIncludedDrafts.filter(draft => getDraftIssues(
            draft, bulkAllowedGrades, bulkAllowedRouteTypes, bulkPasswordMinLength,
            duplicateRowsByEmail.get(String(draft.correo || '').trim().toLowerCase()) || ''
        ).length > 0).length;
    }, [bulkDrafts, bulkIncludedDrafts, bulkAllowedGrades, bulkAllowedRouteTypes, bulkPasswordMinLength]);
    const bulkProblemCount = bulkDrafts.filter(draft => draft.hadIssues).length;
    const bulkAutoSkippedCount = bulkDrafts.filter(draft => draft.skip && draft.autoSkipped).length;
    const bulkExistingCount = useMemo(
        () => bulkIncludedDrafts.filter(draft => getExistingChanges(draft)).length,
        [bulkIncludedDrafts]
    );

    const resetBulkState = () => {
        setBulkFile(null);
        setBulkDrafts([]);
        setBulkAllowedGrades([]);
        setBulkAllowedRouteTypes([]);
        setBulkPasswordMinLength(0);
        setBulkCicloLabel('');
        setBulkPendingFamilies([]);
    };

    const closeBulkDialog = () => {
        setBulkConfirmCancelOpen(false);
        setOpenBulkDialog(false);
        resetBulkState();
    };

    // Con familias sin guardar, cerrar el dialogo pide confirmacion: cancelar descarta toda la carga.
    const handleRequestCloseBulkDialog = () => {
        if (bulkSaving || bulkCompleting) return;
        if (bulkHasDrafts || bulkPendingFamilies.length > 0) setBulkConfirmCancelOpen(true);
        else closeBulkDialog();
    };

    const handleBulkUpload = async () => {
        if (!bulkFile) return;
        if (!canCreateUsersInManagedSchool) {
            setSnackbar({ open: true, message: newUserCreationDisabledMessage, severity: 'warning' });
            return;
        }

        setBulkLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', bulkFile);
            // Adjuntar el colegio gestionado para que el backend pueda enlazar los padres automáticamente
            const schoolToAttach = currentSchool?.id || schoolId || '';
            if (schoolToAttach) formData.append('schoolId', String(schoolToAttach));

            // El backend solo analiza el archivo: no guarda nada hasta confirmar con "Guardar".
            const response = await api.post('/parents/bulk-upload', formData, {
                headers: {
                    Authorization: `Bearer ${auth.token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const drafts = Array.isArray(response.data?.drafts) ? response.data.drafts : [];
            if (drafts.length === 0) {
                setSnackbar({ open: true, message: 'No se encontraron familias en la hoja "Padres" del archivo.', severity: 'warning' });
                return;
            }

            setBulkAllowedGrades(Array.isArray(response.data?.allowedGrades) ? response.data.allowedGrades : []);
            setBulkAllowedRouteTypes(Array.isArray(response.data?.allowedRouteTypes) ? response.data.allowedRouteTypes : []);
            setBulkPasswordMinLength(Number(response.data?.passwordMinLength) || 0);
            setBulkCicloLabel(response.data?.cicloLabel || '');
            lookedUpBulkEmails.current = {};
            setBulkDrafts(drafts.map(({ issues, ...draft }) => {
                const hadIssues = Array.isArray(issues) && issues.length > 0;
                // Una familia que ya existe y no cambia nada no se procesa: se omite sola (se puede volver a incluir).
                const autoSkipped = !hadIssues && hasNothingToUpdate(draft);
                return {
                    ...draft,
                    _key: `${draft.hoja}-${draft.row}`,
                    analyzedCorreo: String(draft.correo || '').trim().toLowerCase(),
                    hadIssues,
                    skip: autoSkipped,
                    autoSkipped,
                    serverIssue: null
                };
            }));
        } catch (err) {
            console.error('Error in bulk upload:', err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || 'Error al analizar el archivo',
                severity: 'error'
            });
        } finally {
            setBulkLoading(false);
        }
    };

    const handleToggleBulkDraftSkip = useCallback((key) => {
        setBulkDrafts(prev => prev.map(draft => (draft._key === key ? { ...draft, skip: !draft.skip } : draft)));
    }, []);

    // Completa solo los pasos pendientes de familias YA guardadas (pagos, estado de servicio, correos), sin reprocesarlas.
    const handleCompletePendingSteps = async () => {
        const schoolToAttach = currentSchool?.id || schoolId || '';
        if (!schoolToAttach) {
            setSnackbar({ open: true, message: 'No se pudo determinar el colegio.', severity: 'error' });
            return;
        }

        const canResetPassword = hasPermission('padres-restablecer-password');
        const items = bulkPendingFamilies
            .map(family => ({
                correo: family.correo,
                steps: family.steps.filter(step => step.status === 'failed' && (step.step !== 'welcomeEmail' || canResetPassword)).map(step => step.step)
            }))
            .filter(item => item.steps.length > 0);
        if (items.length === 0) return;

        setBulkCompleting(true);
        try {
            const resultsByEmail = new Map();
            for (let start = 0; start < items.length; start += 100) {
                const response = await api.post('/parents/bulk-upload/complete', {
                    schoolId: schoolToAttach,
                    items: items.slice(start, start + 100)
                }, { headers: { Authorization: `Bearer ${auth.token}` } });
                (response.data?.results || []).forEach(result => resultsByEmail.set(result.correo, result.steps || []));
            }

            // 'ok' = completado y sale de la lista; lo demás se queda con su motivo ('failed' se puede reintentar).
            let completedCount = 0;
            setBulkPendingFamilies(prev => prev
                .map(family => {
                    const results = resultsByEmail.get(String(family.correo || '').trim().toLowerCase());
                    if (!results) return family;
                    return {
                        ...family,
                        steps: family.steps
                            .map(step => {
                                const result = results.find(item => item.step === step.step);
                                if (!result) return step;
                                return result.status === 'ok' ? null : { ...step, status: result.status, message: result.message || null };
                            })
                            .filter(Boolean)
                    };
                })
                .filter(family => family.steps.length > 0));

            resultsByEmail.forEach(results => results.forEach(result => { if (result.status === 'ok') completedCount += 1; }));
            const totalAsked = items.reduce((total, item) => total + item.steps.length, 0);
            setSnackbar({
                open: true,
                message: completedCount === totalAsked
                    ? `${completedCount} paso(s) completado(s).`
                    : `${completedCount} de ${totalAsked} paso(s) completado(s). Revisa los que siguen pendientes.`,
                severity: completedCount === totalAsked ? 'success' : 'warning'
            });
            if (completedCount > 0) fetchUsers();
        } catch (err) {
            console.error('Error completing bulk pending steps:', err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || 'No se pudieron completar los pasos pendientes. Inténtalo de nuevo.',
                severity: 'error'
            });
        } finally {
            setBulkCompleting(false);
        }
    };

    const handleSaveBulkDrafts = async () => {
        if (bulkIncludedDrafts.length === 0 || bulkUnresolvedCount > 0) return;

        const schoolToAttach = currentSchool?.id || schoolId || '';
        if (!schoolToAttach) {
            setSnackbar({ open: true, message: 'No se pudo determinar el colegio.', severity: 'error' });
            return;
        }

        const authHeaders = { headers: { Authorization: `Bearer ${auth.token}` } };
        const draftsToSave = bulkIncludedDrafts;
        setBulkSaving(true);
        setBulkSaveProgress({ done: 0, total: draftsToSave.length });

        // Se guarda por bloques: una sola petición con cientos de familias dura minutos (correos, pagos) y el proxy o el
        // navegador pueden cortarla aunque el servidor siga guardando. Así cada petición dura segundos y hay progreso.
        // Un bloque que falla no detiene a los demás; lo guardado sale de la lista y lo demás se queda para reintentarlo.
        let successCount = 0;
        const failedByKey = new Map();    // rechazadas por el servidor, con su motivo
        const answeredKeys = new Set();   // familias de bloques que el servidor respondió
        const uncertainKeys = new Set();  // bloques sin respuesta (red, tiempo de espera, 5xx): pudieron guardarse en parte
        let blockedMessage = null;        // un 4xx (permiso, límite de uso, sesión) se repetiría igual en cada bloque
        const pendingFamilies = [];       // guardadas con algún paso posterior sin completar

        try {
            for (let start = 0; start < draftsToSave.length; start += BULK_COMMIT_CHUNK_SIZE) {
                const chunk = draftsToSave.slice(start, start + BULK_COMMIT_CHUNK_SIZE);
                try {
                    const response = await api.post('/parents/bulk-upload/commit', {
                        schoolId: schoolToAttach,
                        drafts: chunk.map(toBulkServerDraft)
                    }, authHeaders);
                    successCount += response.data?.successCount || 0;
                    (Array.isArray(response.data?.failedDrafts) ? response.data.failedDrafts : [])
                        .forEach(draft => failedByKey.set(`${draft.hoja}-${draft.row}`, draft));
                    chunk.forEach(draft => answeredKeys.add(draft._key));
                    (Array.isArray(response.data?.savedDrafts) ? response.data.savedDrafts : []).forEach(saved => pendingFamilies.push({
                        key: `${saved.hoja}-${saved.row}`, row: saved.row, nombre: saved.nombre, correo: saved.correo, steps: saved.pending
                    }));
                } catch (err) {
                    console.error('Error saving bulk families:', err);
                    const status = err?.response?.status;
                    if (!status || status >= 500 || status === 408) {
                        chunk.forEach(draft => uncertainKeys.add(draft._key));
                    } else {
                        blockedMessage = err?.response?.data?.message || 'El servidor rechazó el guardado.';
                        break;
                    }
                }
                setBulkSaveProgress({ done: Math.min(start + BULK_COMMIT_CHUNK_SIZE, draftsToSave.length), total: draftsToSave.length });
            }

            if (successCount > 0) fetchUsers();
            if (pendingFamilies.length > 0) setBulkPendingFamilies(prev => [...prev, ...pendingFamilies]);

            if (!blockedMessage && failedByKey.size === 0 && uncertainKeys.size === 0 && pendingFamilies.length === 0) {
                const skippedCount = bulkDrafts.filter(draft => draft.skip).length;
                setSnackbar({
                    open: true,
                    message: `${successCount} familia(s) guardada(s)${skippedCount > 0 ? `; ${skippedCount} omitida(s)` : ''}.`,
                    severity: 'success'
                });
                closeBulkDialog();
                return;
            }

            // Las rechazadas y las de resultado incierto se vuelven a analizar para refrescar lo que el sistema ya
            // tiene de ellas: así se ve cuáles sí llegaron a guardarse y cuáles hay que reenviar.
            const toRecheck = draftsToSave.filter(draft => failedByKey.has(draft._key) || uncertainKeys.has(draft._key));
            const rechecked = new Map();
            let recheckFailed = false;
            try {
                for (let start = 0; start < toRecheck.length; start += 100) {
                    const chunk = toRecheck.slice(start, start + 100);
                    const response = await api.post('/parents/bulk-upload/analyze', {
                        schoolId: schoolToAttach,
                        drafts: chunk.map(toBulkServerDraft)
                    }, authHeaders);
                    (response.data?.drafts || []).forEach((result, index) => rechecked.set(chunk[index]._key, result));
                }
            } catch (err) {
                console.error('Error re-analyzing bulk families:', err);
                recheckFailed = true;
            }

            const savedKeys = new Set([...answeredKeys].filter(key => !failedByKey.has(key)));
            // Función pura (React puede ejecutar el updater más de una vez): deja la tarjeta lista para corregirla o reenviarla.
            const reconcileDraft = (draft) => {
                const failed = failedByKey.get(draft._key);
                const uncertain = uncertainKeys.has(draft._key);
                if (!failed && !uncertain) return draft; // omitida, o no llegó a enviarse
                const fresh = rechecked.get(draft._key);
                let next = { ...draft, hadIssues: true, ...(fresh ? { existing: fresh.existing, otherSchool: fresh.otherSchool } : {}) };
                if (failed) {
                    // Cualquier rechazo del servidor debe verse: si solo hay issues de campo que el formulario no
                    // detecta, la tarjeta quedaría en "Listo" sin explicar por qué no se guardó.
                    const reason = (failed.issues || []).find(issue => issue.field === 'general') || (failed.issues || [])[0];
                    next.serverIssue = reason?.message || null;
                }
                if (uncertain) {
                    // No se sabe si el restablecimiento de contraseña llegó a aplicarse: se desmarca para no enviar dos temporales.
                    next = {
                        ...next,
                        resetPassword: false,
                        infoNotice: recheckFailed
                            ? 'El guardado de este grupo se interrumpió y no se pudo verificar si esta familia se guardó. Se desmarcó "Restablecer contraseña" por si ya se había aplicado.'
                            : 'El guardado de este grupo se interrumpió; la familia se volvió a analizar. Se desmarcó "Restablecer contraseña" por si ya se había aplicado: márcala de nuevo si hace falta.'
                    };
                    if (fresh && hasNothingToUpdate(next)) {
                        next = { ...next, skip: true, hadIssues: false, infoNotice: 'Esta familia ya figura guardada: el guardado se completó antes de interrumpirse. Se omite automáticamente.' };
                    }
                }
                return next;
            };
            const alreadySavedCount = draftsToSave.filter(draft => uncertainKeys.has(draft._key) && reconcileDraft(draft).skip).length;
            setBulkDrafts(prev => prev.filter(draft => !savedKeys.has(draft._key)).map(reconcileDraft));

            const parts = [`${successCount} familia(s) guardada(s).`];
            if (pendingFamilies.length > 0) parts.push(`${pendingFamilies.length} con pasos por completar (pagos o correo): revísalas arriba.`);
            if (failedByKey.size > 0) parts.push(`${failedByKey.size} rechazada(s): corrígelas para reenviarlas.`);
            if (uncertainKeys.size > 0) {
                parts.push(`${uncertainKeys.size} con resultado incierto por una interrupción${recheckFailed ? ' (no se pudieron verificar)' : ` (${alreadySavedCount} ya figuran guardadas)`}.`);
            }
            if (blockedMessage) {
                const notSent = draftsToSave.filter(draft => !answeredKeys.has(draft._key) && !uncertainKeys.has(draft._key)).length;
                parts.push(`El guardado se detuvo: ${blockedMessage} ${notSent} familia(s) no se enviaron.`);
            }
            setSnackbar({ open: true, message: parts.join(' '), severity: blockedMessage || uncertainKeys.size > 0 ? 'error' : 'warning' });
        } finally {
            setBulkSaving(false);
        }
    };

    const currentSchool = stateSchool ? {
        ...stateSchool,
        ...(stateCicloEscolar ? { cicloEscolar: stateCicloEscolar } : {})
    } : null;
    const schoolRecordFromList = Array.isArray(schools)
        ? schools.find(s => String(s.id) === String(schoolId || currentSchool?.id || ''))
        : null;
    const managedSchool = { ...(schoolRecordFromList || {}), ...(currentSchool || {}) };
    const canCreateUsersInManagedSchool = managedSchool.canCreateNewUsers !== false;
    const newUserCreationDisabledMessage = managedSchool.newUserCreationMessage || 'Este colegio pertenece a un ciclo anterior. Para crear nuevos usuarios, usa el colegio del ciclo más reciente.';

    // Obtener opciones de grados del colegio gestionado (soporta varios formatos)
    const availableGrades = (() => {
        let sch = null;

        // Try find school by the user's school id (families: schoolId; staff: school)
        const selectedUserSchoolId = selectedUser?.schoolId || selectedUser?.school;
        if (selectedUserSchoolId) {
            if (Array.isArray(schools)) {
                sch = schools.find(s => String(s.id) === String(selectedUserSchoolId));
            }
            // as fallback, if currentSchool matches the id
            if (!sch && currentSchool && String(currentSchool.id) === String(selectedUserSchoolId)) {
                sch = currentSchool;
            }
        }

        // fallback to currentSchool
        if (!sch && currentSchool) sch = currentSchool;

        // fallback to schoolId param
        if (!sch && schoolId && Array.isArray(schools)) {
            sch = schools.find(s => String(s.id) === String(schoolId));
        }

        const gradesRaw = sch?.grades;
        if (!gradesRaw) return [];

        let parsed = [];
        if (typeof gradesRaw === 'string') {
            // try parse JSON first
            try {
                parsed = JSON.parse(gradesRaw);
            } catch (err) {
                // if not JSON, maybe comma-separated
                parsed = gradesRaw.split(',').map(x => x.trim()).filter(Boolean).map(name => ({ name }));
            }
        } else if (Array.isArray(gradesRaw)) {
            parsed = gradesRaw;
        } else {
            return [];
        }

        // Normalize to array of strings
        return parsed
            .map(g => {
                if (!g) return '';
                if (typeof g === 'string') return g;
                if (typeof g === 'object') return g.name ?? g.label ?? JSON.stringify(g);
                return String(g);
            })
            .filter(Boolean);
    })();

    // apply sorting to filteredUsers before pagination
    const sortedUsers = useMemo(() => {
        if (!sortBy) return filteredUsers;
        const copy = filteredUsers.slice();
        copy.sort((a, b) => {
            let va = '';
            let vb = '';
            switch (sortBy) {
                case 'status':
                    va = getUserStatus(a);
                    vb = getUserStatus(b);
                    break;
                case 'updatedAt':
                    va = getFamilyUpdatedAt(a) || a.updatedAt || a.createdAt || '';
                    vb = getFamilyUpdatedAt(b) || b.updatedAt || b.createdAt || '';
                    break;
                case 'createdAt':
                    va = getFamilyCreatedAt(a) || a.createdAt || '';
                    vb = getFamilyCreatedAt(b) || b.createdAt || '';
                    break;
                case 'familyLastName':
                    va = a.FamilyDetail?.familyLastName || a.familyLastName || '';
                    vb = b.FamilyDetail?.familyLastName || b.familyLastName || '';
                    break;
                case 'name':
                    va = a.name || '';
                    vb = b.name || '';
                    break;
                case 'email':
                    va = getVisibleUserEmail(a);
                    vb = getVisibleUserEmail(b);
                    break;
                case 'role':
                    va = (roleOptions.find(r => r.id === a.roleId)?.name) || '';
                    vb = (roleOptions.find(r => r.id === b.roleId)?.name) || '';
                    break;
                case 'students':
                    va = String(getStudentsCount(a));
                    vb = String(getStudentsCount(b));
                    break;
                case 'serviceStatus':
                    va = a.familyServiceStatus?.status || '';
                    vb = b.familyServiceStatus?.status || '';
                    break;
                default:
                    va = '';
                    vb = '';
            }

            // try numeric compare for updatedAt/createdAt or students
            if (sortBy === 'updatedAt' || sortBy === 'createdAt') {
                const da = new Date(va).getTime() || 0;
                const db = new Date(vb).getTime() || 0;
                return sortOrder === 'asc' ? da - db : db - da;
            }

            if (!isNaN(Number(va)) && !isNaN(Number(vb))) {
                return sortOrder === 'asc' ? Number(va) - Number(vb) : Number(vb) - Number(va);
            }

            // string compare
            const cmp = va.toString().toLowerCase().localeCompare(vb.toString().toLowerCase());
            return sortOrder === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [filteredUsers, sortBy, sortOrder, getUserStatus]);

    const paginatedUsers = sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Fuente de la lista para las consultas asíncronas: así una respuesta tardía puede descartarse si el correo ya cambió.
    const bulkDraftsRef = useRef([]);
    bulkDraftsRef.current = bulkDrafts;

    // Al cambiar el correo de una familia, lo que el análisis del archivo dijo del correo anterior ya no vale:
    // se consulta si el nuevo ya es una cuenta, si tiene familia en este ciclo o si es de otro colegio.
    const refreshBulkDraftAccount = useCallback(async (key, email) => {
        const normalizedEmail = String(email || '').trim().toLowerCase();
        const schoolToAttach = currentSchool?.id || schoolId || '';
        const current = bulkDraftsRef.current.find(draft => draft._key === key);
        if (!current || !schoolToAttach) return;

        let existing = null;
        let otherSchool = null;
        if (normalizedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
            try {
                const response = await api.post('/parents/bulk-upload/lookup', {
                    schoolId: schoolToAttach,
                    drafts: [{ hoja: current.hoja, row: current.row, correo: normalizedEmail }]
                }, { headers: { Authorization: `Bearer ${auth.token}` } });
                existing = response.data?.drafts?.[0]?.existing || null;
                otherSchool = response.data?.drafts?.[0]?.otherSchool || null;
            } catch (err) {
                console.error('Error consultando el correo de la familia:', err);
                // Sin respuesta no se sabe nada del correo; el guardado lo vuelve a validar en el servidor.
                setSnackbar({
                    open: true,
                    message: `${err?.response?.data?.message || 'No se pudo verificar el correo.'} Se volverá a validar al guardar.`,
                    severity: 'warning'
                });
            }
        }

        setBulkDrafts(prev => prev.map(draft => {
            if (draft._key !== key || String(draft.correo || '').trim().toLowerCase() !== normalizedEmail) return draft;
            return { ...draft, existing, otherSchool };
        }));
    }, [auth.token, currentSchool?.id, schoolId]);

    const handleChangeBulkDraft = useCallback((key, patch) => {
        setBulkDrafts(prev => prev.map(draft => (draft._key === key ? { ...draft, ...patch } : draft)));
    }, []);

    // La consulta se hace solo cuando el campo del correo pierde el foco (no mientras se escribe) y únicamente si el
    // correo es distinto al último consultado; por defecto, el que ya analizó el archivo.
    const lookedUpBulkEmails = useRef({});
    const handleBulkDraftEmailBlur = useCallback((key, email) => {
        const normalizedEmail = String(email || '').trim().toLowerCase();
        const current = bulkDraftsRef.current.find(draft => draft._key === key);
        if (!current) return;
        const lastLookedUp = key in lookedUpBulkEmails.current ? lookedUpBulkEmails.current[key] : current.analyzedCorreo;
        if (normalizedEmail === lastLookedUp) return;
        lookedUpBulkEmails.current[key] = normalizedEmail;
        refreshBulkDraftAccount(key, normalizedEmail);
    }, [refreshBulkDraftAccount]);

    const handleChangeRowsPerPage = (event) => {
    const v = parseInt(event.target.value, 10);
    setRowsPerPage(Number.isNaN(v) ? 10 : v);
    setPage(0);
    };

    return (
        <PageContainer>
            {/* Header */}
            <HeaderCard>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={handleBackToDashboard}
                            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Volver al Dashboard
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SchoolIcon sx={{ fontSize: 40 }} />
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Familias - {currentSchool?.name || 'Cargando...'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip 
                                    icon={<CalendarToday />}
                                    label={`Ciclo Escolar ${currentCycleLabel || ''}`}
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                        {users.length} familias encontradas
                                    </Typography>
                                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                        {(() => {
                                            try {
                                                const total = countStudents(users);
                                                return `${total} alumnos`;
                                            } catch (e) {
                                                return `0 alumnos`;
                                            }
                                        })()}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </HeaderCard>

            {/* Filtros y búsqueda */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Buscar por nombre, apellido o email..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSearch}
                                sx={{ height: '56px' }}
                            >
                                Buscar
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    label="Estado"
                                    startAdornment={<FilterList />}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="Nuevo">Nuevo</MenuItem>
                                    <MenuItem value="Duplicado">Duplicado</MenuItem>
                                    <MenuItem value="Actualizado">Actualizado</MenuItem>
                                    <MenuItem value="Sin asignaciones">Sin asignaciones</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>Estado del Servicio</InputLabel>
                                <Select
                                    value={serviceStatusFilter}
                                    onChange={(e) => setServiceStatusFilter(e.target.value)}
                                    label="Estado del Servicio"
                                    startAdornment={<FilterList />}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="ACTIVE">Activo</MenuItem>
                                    <MenuItem value="PAUSED">Pausado</MenuItem>
                                    <MenuItem value="SUSPENDED">Suspendido</MenuItem>
                                    <MenuItem value="INACTIVE">Inactivo</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* Role filter removed: this view only handles Padres */}
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={handleClearFilters}
                            >
                                Limpiar
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Botones de Acción */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    {!canCreateUsersInManagedSchool && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {newUserCreationDisabledMessage}
                        </Alert>
                    )}
                    <Grid container spacing={2}>
                        <PermissionGuard permission="padres-carga-masiva">
                            <Grid item xs={12} md={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<FileUpload />}
                                    fullWidth
                                    onClick={() => setOpenBulkDialog(true)}
                                    disabled={!canCreateUsersInManagedSchool}
                                >
                                    Carga Masiva
                                </Button>
                            </Grid>
                        </PermissionGuard>
                        <PermissionGuard permission="horarios-carga-masiva">
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<FileUpload />}
                                fullWidth
                                onClick={() => setOpenBulkStopsEditor(true)}
                            >
                                Carga de Paradas
                            </Button>
                        </Grid>
                        </PermissionGuard>
                        <PermissionGuard permission="usuarios-crear">
                            <Grid item xs={12} md={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Add />}
                                    fullWidth
                                    onClick={handleAddUser}
                                    disabled={!canCreateUsersInManagedSchool}
                                >
                                    Añadir Familia
                                </Button>
                            </Grid>
                        </PermissionGuard>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<GetApp />}
                                fullWidth
                                onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                            >
                                Descargar
                            </Button>
                            <Menu
                                anchorEl={downloadMenuAnchor}
                                open={Boolean(downloadMenuAnchor)}
                                onClose={() => setDownloadMenuAnchor(null)}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setDownloadMenuAnchor(null);
                                        setOpenDownloadDialog(true);
                                    }}
                                >
                                    Descargar Familias
                                </MenuItem>
                                <MenuItem
                                    disabled={routeReportLoading}
                                    onClick={() => {
                                        setDownloadMenuAnchor(null);
                                        handleDownloadRouteReport(currentSchool?.id || schoolId);
                                    }}
                                >
                                    Descargar Reporte de Paradas
                                </MenuItem>
                            </Menu>
                        </Grid>

                        <Dialog open={openDownloadDialog} onClose={() => setOpenDownloadDialog(false)}>
                            <DialogTitle>Descargar Familias</DialogTitle>
                            <DialogContent>
                                <DialogContentText>Selecciona qué familias deseas descargar (puedes elegir varias opciones):</DialogContentText>
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel id="parents-download-select-label">Opciones</InputLabel>
                                    <Select
                                        labelId="parents-download-select-label"
                                        multiple
                                        value={downloadChoice}
                                        label="Opciones"
                                        onChange={(e) => {
                                            const raw = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                                            // "Todas" es excluyente: no combina con los demás filtros
                                            if (raw.includes('all')) {
                                                setDownloadChoice(downloadChoice.includes('all') ? raw.filter((v) => v !== 'all') : ['all']);
                                            } else {
                                                setDownloadChoice(raw);
                                            }
                                        }}
                                        renderValue={(selected) => selected.map((v) => DOWNLOAD_FAMILY_OPTION_LABELS[v] || v).join(', ')}
                                    >
                                        {DOWNLOAD_FAMILY_OPTIONS.map((opt) => {
                                            const allSelected = downloadChoice.includes('all');
                                            const disabled = opt.value === 'all'
                                                ? (downloadChoice.length > 0 && !allSelected)
                                                : allSelected;
                                            return (
                                                <MenuItem key={opt.value} value={opt.value} disabled={disabled}>
                                                    <Checkbox checked={downloadChoice.indexOf(opt.value) > -1} />
                                                    <ListItemText primary={opt.label} />
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDownloadDialog(false)}>Cancelar</Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<GetApp />}
                                    disabled={downloadChoice.length === 0 || routeReportLoading}
                                    onClick={async () => {
                                        await handleDownloadCombinedFamilies(downloadChoice, currentSchool?.id || schoolId);
                                        setOpenDownloadDialog(false);
                                    }}
                                >
                                    Descargar
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </CardContent>
            </Card>

            {/* Tabla de usuarios */}
            <Card>
                <CardContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">
                                                <TableSortLabel
                                                    active={sortBy === 'serviceStatus'}
                                                    direction={sortBy === 'serviceStatus' ? sortOrder : 'asc'}
                                                    onClick={() => handleSortChange('serviceStatus')}
                                                >
                                                    Estado del Servicio
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortBy === 'familyLastName'}
                                                    direction={sortBy === 'familyLastName' ? sortOrder : 'asc'}
                                                    onClick={() => handleSortChange('familyLastName')}
                                                >
                                                    Apellido Familia
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortBy === 'students'}
                                                    direction={sortBy === 'students' ? sortOrder : 'asc'}
                                                    onClick={() => handleSortChange('students')}
                                                >
                                                    Cant. Hijos
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortBy === 'email'}
                                                    direction={sortBy === 'email' ? sortOrder : 'asc'}
                                                    onClick={() => handleSortChange('email')}
                                                >
                                                    Correo
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortBy === 'createdAt'}
                                                    direction={sortBy === 'createdAt' ? sortOrder : 'asc'}
                                                    onClick={() => handleSortChange('createdAt')}
                                                >
                                                    Inscrito el
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortBy === 'updatedAt'}
                                                    direction={sortBy === 'updatedAt' ? sortOrder : 'asc'}
                                                    onClick={() => handleSortChange('updatedAt')}
                                                >
                                                    Fecha de actualización
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortBy === 'status'}
                                                    direction={sortBy === 'status' ? sortOrder : 'asc'}
                                                    onClick={() => handleSortChange('status')}
                                                >
                                                    Estado
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                Acciones
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedUsers.map((user) => {
                                            const serviceStatus = user.familyServiceStatus?.status || 'ACTIVE';
                                            const userStatus = getUserStatus(user);
                                            const getServiceStatusChip = () => {
                                                if (serviceStatus === 'ACTIVE') {
                                                    return <Chip label="Activo" size="small" color="success" />;
                                                } else if (serviceStatus === 'PAUSED') {
                                                    return <Chip label="Pausado" size="small" color="warning" />;
                                                } else if (serviceStatus === 'SUSPENDED') {
                                                    return <Chip label="Suspendido" size="small" color="error" />;
                                                } else if (serviceStatus === 'INACTIVE') {
                                                    return <Chip label="Inactivo" size="small" sx={{ backgroundColor: '#9e9e9e', color: 'white' }} />;
                                                }
                                                return <Chip label="-" size="small" />;
                                            };
                                            
                                            return (
                                            <TableRow key={user.id} hover>
                                                <TableCell align="center">
                                                    {getServiceStatusChip()}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        {user.FamilyDetail?.familyLastName || user.familyLastName || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStudentsCount(user)}
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Email fontSize="small" color="action" />
                                                        {getVisibleUserEmail(user) || 'N/A'}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {(() => {
                                                            const famCreated = getFamilyCreatedAt(user);
                                                            if (!famCreated) return '-';
                                                            const d = new Date(famCreated);
                                                            if (isNaN(d.getTime())) return '-';
                                                            const dd = String(d.getDate()).padStart(2, '0');
                                                            const mm = String(d.getMonth() + 1).padStart(2, '0');
                                                            const yyyy = d.getFullYear();
                                                            return `${dd}/${mm}/${yyyy}`;
                                                        })()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {(() => {
                                                            const famUpdated = getFamilyUpdatedAt(user);
                                                            const d = new Date(famUpdated || user.updatedAt || user.createdAt);
                                                            if (isNaN(d.getTime())) return '-';
                                                            const dd = String(d.getDate()).padStart(2, '0');
                                                            const mm = String(d.getMonth() + 1).padStart(2, '0');
                                                            const yyyy = d.getFullYear();
                                                            const hh = String(d.getHours()).padStart(2, '0');
                                                            const min = String(d.getMinutes()).padStart(2, '0');
                                                            return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
                                                        })()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {userStatus === '-' ? (
                                                        <Typography variant="body2" color="textSecondary">-</Typography>
                                                    ) : (
                                                        <Chip
                                                            label={userStatus}
                                                            color={getStatusColor(userStatus)}
                                                            size="small"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                        <PermissionGuard permission="usuarios-editar">
                                                            <IconButton size="small" onClick={() => handleEditClick(user)}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </PermissionGuard>

                                                        {Number(user.roleId) === 3 && (
                                                            <>
                                                                {(() => {
                                                                    const s = getBusAssignStatus(user);
                                                                    const title = s === 'all' ? 'Todos los estudiantes tienen asignación' : (s === 'some' ? 'Algunos estudiantes sin asignación' : 'Ningún estudiante tiene asignación');
                                                                    if (s === 'none') {
                                                                        return (
                                                                            <IconButton size="small" onClick={() => handleAssignBuses(user)} title={title}>
                                                                                <DirectionsBus fontSize="small" />
                                                                            </IconButton>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleAssignBuses(user)}
                                                                            sx={(theme) => {
                                                                                if (s === 'all') return {
                                                                                    bgcolor: theme.palette.success.main,
                                                                                    color: theme.palette.common.white,
                                                                                    borderRadius: 1,
                                                                                    '&:hover': { bgcolor: theme.palette.success.dark }
                                                                                };
                                                                                // s === 'some'
                                                                                return {
                                                                                    bgcolor: theme.palette.warning.main,
                                                                                    color: theme.palette.common.white,
                                                                                    borderRadius: 1,
                                                                                    '&:hover': { bgcolor: theme.palette.warning.dark }
                                                                                };
                                                                            }}
                                                                            title={title}
                                                                        >
                                                                            <DirectionsBus fontSize="small" />
                                                                        </IconButton>
                                                                    );
                                                                })()}
                                                                {puedeEnviarMensajeFamilia && (
                                                                    <IconButton size="small" onClick={() => handleSendCommunication(user)} title="Enviar comunicación">
                                                                        <Mail fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                            </>
                                                        )}

                                                        {(() => {
                                                            const isInactive = serviceStatus === 'INACTIVE';
                                                            // Activar y suspender son rutas distintas de /service-status.
                                                            if (isInactive) {
                                                                return hasPermission('usuarios-activar-servicio') ? (
                                                                    <IconButton size="small" title="Activar familia" onClick={() => handleActivateClick(user)} color="success">
                                                                        <ToggleOff fontSize="small" />
                                                                    </IconButton>
                                                                ) : null;
                                                            }
                                                            return hasPermission('usuarios-suspender-servicio') ? (
                                                                <IconButton size="small" title="Suspender familia" onClick={() => handleSuspendClick(user)} color="warning">
                                                                    <ToggleOn fontSize="small" />
                                                                </IconButton>
                                                            ) : null;
                                                        })()}

                                                        <PermissionGuard permission="usuarios-eliminar">
                                                            <IconButton size="small" onClick={() => handleDeleteClick(user)} color="error">
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </PermissionGuard>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}

                                        {paginatedUsers.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        No se encontraron usuarios con los filtros aplicados
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={sortedUsers.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5,10,25,50]}
                                />
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={openBulkDialog} onClose={handleRequestCloseBulkDialog} maxWidth={bulkHasDrafts ? 'md' : 'sm'} fullWidth>
                <DialogTitle>Carga Masiva de Familias</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Sube un archivo Excel. Usa la plantilla oficial.<br />
                        <br />
                        La lista de Grados se encuentra en la hoja "Listas" de la plantilla.<br />
                        <br />
                        El límite de archivo es 5 MB.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            sx={{ mr: 2 }}
                            color="success"
                            onClick={handleDownloadParentsTemplate}
                        >
                            Descargar Plantilla
                        </Button>
                        <Button variant="outlined" component="label" startIcon={<FileUpload />} disabled={bulkLoading || bulkSaving}>
                            Seleccionar Archivo
                            <input
                                type="file"
                                hidden
                                onChange={(e) => {
                                    setBulkFile(e.target.files[0]);
                                    setBulkDrafts([]);
                                }}
                                accept=".xlsx,.xls,.csv"
                            />
                        </Button>
                    </Box>
                    {bulkFile && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Archivo seleccionado: {bulkFile.name}
                        </Typography>
                    )}
                    {bulkLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ ml: 2 }}>
                                Procesando archivo...
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ mt: bulkPendingFamilies.length > 0 ? 2 : 0 }}>
                        <BulkPendingSteps
                            families={bulkPendingFamilies}
                            canResetPassword={hasPermission('padres-restablecer-password')}
                            completing={bulkCompleting}
                            onComplete={handleCompletePendingSteps}
                            onDismiss={() => setBulkPendingFamilies([])}
                        />
                    </Box>
                    {bulkSaving && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress
                                variant="determinate"
                                value={bulkSaveProgress.total > 0 ? (bulkSaveProgress.done / bulkSaveProgress.total) * 100 : 0}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Guardando familias: {bulkSaveProgress.done} de {bulkSaveProgress.total}. No cierres esta ventana hasta que termine.
                            </Typography>
                        </Box>
                    )}
                    {bulkHasDrafts && (
                        <Box sx={{ mt: 3 }}>
                            <Alert severity={bulkProblemCount > 0 ? 'warning' : 'info'} sx={{ mb: 1 }}>
                                Aún no se guardó ninguna familia{bulkCicloLabel ? ` (ciclo escolar ${bulkCicloLabel})` : ''}. Se encontraron {bulkDrafts.length} familia(s)
                                {bulkProblemCount > 0
                                    ? `; ${bulkProblemCount} tienen datos por corregir (abajo). Corrígelas u omítelas para poder guardar.`
                                    : ' y todas están listas para guardar.'}
                                {bulkExistingCount > 0 && ` ${bulkExistingCount} ya existen y se actualizarán: revisa sus cambios con "Ver cambios".`}
                                {bulkAutoSkippedCount > 0 && ` ${bulkAutoSkippedCount} ya existen sin cambios y se omiten automáticamente.`}
                            </Alert>
                            <BulkFamilyDraftsReview
                                drafts={bulkDrafts}
                                allowedGrades={bulkAllowedGrades}
                                allowedRouteTypes={bulkAllowedRouteTypes}
                                passwordMinLength={bulkPasswordMinLength}
                                canResetPassword={hasPermission('padres-restablecer-password')}
                                cicloLabel={bulkCicloLabel}
                                onChangeDraft={handleChangeBulkDraft}
                                onToggleSkip={handleToggleBulkDraftSkip}
                                onEmailBlur={handleBulkDraftEmailBlur}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRequestCloseBulkDialog} disabled={bulkSaving}>Cancelar</Button>
                    {hasPermission('padres-carga-masiva') && (bulkHasDrafts ? (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={bulkSaving || bulkIncludedDrafts.length === 0 || bulkUnresolvedCount > 0}
                            onClick={handleSaveBulkDrafts}
                        >
                            {bulkSaving
                                ? `Guardando ${bulkSaveProgress.done} de ${bulkSaveProgress.total}...`
                                : bulkIncludedDrafts.length === 0
                                    ? 'No hay familias por guardar'
                                    : bulkUnresolvedCount > 0
                                        ? `Faltan ${bulkUnresolvedCount} por corregir`
                                        : `Guardar ${bulkIncludedDrafts.length} familia(s)`}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!bulkFile || bulkLoading}
                            onClick={handleBulkUpload}
                        >
                            Subir
                        </Button>
                    ))}
                </DialogActions>
            </Dialog>

            <Dialog open={bulkConfirmCancelOpen} onClose={() => setBulkConfirmCancelOpen(false)}>
                <DialogTitle>¿Cancelar la carga masiva?</DialogTitle>
                <DialogContent>
                    {bulkDrafts.length > 0 && (
                        <Typography variant="body1">
                            Hay {bulkDrafts.length} familia(s) sin guardar{bulkUnresolvedCount > 0 ? `, ${bulkUnresolvedCount} de ellas con datos pendientes por corregir` : ''}.
                            Si cancelas, <strong>no se guardará ninguna de ellas</strong> y tendrás que volver a subir el archivo.
                        </Typography>
                    )}
                    {bulkPendingFamilies.length > 0 && (
                        <Typography variant="body1" sx={{ mt: bulkDrafts.length > 0 ? 1.5 : 0 }}>
                            {bulkPendingFamilies.length} familia(s) ya guardada(s) tienen pasos por completar (pagos o correo).
                            Si cancelas, <strong>se perderá este seguimiento</strong>: esas familias quedan guardadas, pero tendrás que completar a mano lo que falte.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkConfirmCancelOpen(false)}>Seguir revisando</Button>
                    <Button color="error" variant="contained" onClick={closeBulkDialog}>Sí, descartar carga</Button>
                </DialogActions>
            </Dialog>

            {/* Editor de carga masiva de paradas */}
            {openBulkStopsEditor && (
                <BulkStopsEditor
                    open={openBulkStopsEditor}
                    onClose={() => setOpenBulkStopsEditor(false)}
                    schoolId={currentSchool?.id || schoolId}
                />
            )}

            {/* Diálogo de confirmación para eliminación de usuario */}
            <Dialog open={openDeleteConfirm} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
                <DialogTitle>Eliminar del ciclo escolar</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro que deseas eliminar a <strong>{selectedUser?.name || selectedUser?.email || selectedUser?.id}</strong> de este colegio y ciclo escolar?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>Cancelar</Button>
                    <PermissionGuard permission="usuarios-eliminar">
                        <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={bulkLoading}>
                            {bulkLoading ? 'Eliminando...' : 'Eliminar'}
                        </Button>
                    </PermissionGuard>
                </DialogActions>
            </Dialog>

            {/* Modal de gestión de estados del servicio (reemplaza activar/suspender) */}
            <UserServiceStatusModal
                open={openServiceStatusModal}
                onClose={() => {
                    setOpenServiceStatusModal(false);
                    setSelectedUser(null);
                }}
                user={selectedUser}
                schoolId={stateSchool?.id || schoolId}
                cicloEscolarId={stateCicloEscolarId}
                onSuccess={handleServiceStatusSuccess}
            />

            {/* Modal para asignar buses */}
            <StudentScheduleModal
                studentId={null}
                students={scheduleModalStudents}
                schoolId={scheduleSchoolId}
                open={openStudentScheduleModal}
                onClose={() => {
                    setOpenStudentScheduleModal(false);
                    setScheduleModalStudents([]);
                    setScheduleSchoolId(null);
                    fetchUsers();
                }}
            />

            {/* Diálogo para envío de comunicación a una familia */}
            <Dialog
                open={openFamilyMessageDialog}
                onClose={handleCloseFamilyMessageDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 18px 60px rgba(0, 0, 0, 0.22)',
                    }
                }}
            >
                <Box sx={{ background: 'linear-gradient(135deg, #16324f 0%, #1e88e5 100%)', color: 'white', px: 3, py: 2.25 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                        Enviar comunicación a la familia
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.92 }}>
                        Escribe el mensaje para <strong>{selectedUser?.name || selectedUser?.email || 'la familia'}</strong> y elige el tipo de envío.
                    </Typography>
                </Box>

                <DialogContent sx={{ p: 3, background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)' }}>
                    {!familyMessageChannel ? (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                                Tipo de envío
                            </Typography>
                            <Grid container spacing={1.5}>
                                <PermissionGuard permission="mail-enviar-familia">
                                <Grid item xs={12} md={6}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => setFamilyMessageChannel('email')}
                                        sx={{
                                            height: 48,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            boxShadow: '0 8px 20px rgba(25, 118, 210, 0.25)',
                                            background: 'linear-gradient(135deg, #1976d2 0%, #4f9df7 100%)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #1565c0 0%, #3b8ce6 100%)',
                                            },
                                        }}
                                    >
                                        Correo electrónico
                                    </Button>
                                </Grid>
                                </PermissionGuard>
                                <PermissionGuard permission="notificaciones-crear">
                                <Grid item xs={12} md={6}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => setFamilyMessageChannel('push')}
                                        sx={{
                                            height: 48,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderWidth: 1.5,
                                            backgroundColor: 'rgba(30, 136, 229, 0.04)',
                                            '&:hover': {
                                                borderWidth: 1.5,
                                                backgroundColor: 'rgba(30, 136, 229, 0.08)',
                                            },
                                        }}
                                    >
                                        Notificación
                                    </Button>
                                </Grid>
                                </PermissionGuard>
                            </Grid>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 1.5, borderRadius: 2, backgroundColor: familyMessageChannel === 'email' ? 'rgba(25,118,210,0.08)' : 'rgba(46,125,50,0.08)', border: '1px solid', borderColor: familyMessageChannel === 'email' ? 'rgba(25,118,210,0.20)' : 'rgba(46,125,50,0.20)' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                            {familyMessageChannel === 'email' ? 'Modo correo' : 'Modo notificación'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {familyMessageChannel === 'email'
                                                ? 'Puedes incluir un archivo adjunto.'
                                                : 'Se enviará como mensaje directo.'}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => setFamilyMessageChannel('')}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Cambiar
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Título"
                                    value={familyMessageTitle}
                                    onChange={(e) => setFamilyMessageTitle(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Mensaje"
                                    value={familyMessageBody}
                                    onChange={(e) => setFamilyMessageBody(e.target.value)}
                                    multiline
                                    minRows={4}
                                    required
                                />
                            </Grid>
                            {familyMessageChannel === 'email' && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            component="label"
                                            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, height: 36 }}
                                        >
                                            Adjuntar archivo
                                            <input
                                                type="file"
                                                hidden
                                                onChange={(e) => setFamilyMessageFile(e.target.files?.[0] || null)}
                                            />
                                        </Button>
                                        {familyMessageFile ? (
                                            <Chip
                                                size="small"
                                                label={familyMessageFile.name}
                                                onDelete={() => setFamilyMessageFile(null)}
                                                sx={{ maxWidth: '100%' }}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                Sin archivo adjunto
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1, backgroundColor: '#fff' }}>
                    <Button
                        onClick={handleCloseFamilyMessageDialog}
                        disabled={familyMessageSending}
                        size="small"
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
                    >
                        Cancelar
                    </Button>
                    {familyMessageChannel && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSendFamilyMessage}
                            disabled={familyMessageSending}
                            size="small"
                            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, px: 2.25, boxShadow: 'none' }}
                        >
                            {familyMessageSending ? 'Enviando...' : 'Enviar'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Diálogo para crear/editar usuario */}
            <Dialog
                open={openEditDialog}
                onClose={() => {
                    setOpenRouteTypeModal(false);
                    setOpenEditDialog(false);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{selectedUser?.id ? 'Editar Familia' : 'Añadir Familia'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {selectedUser?.id
                            ? 'Actualiza la información del usuario.'
                            : 'Completa la información para crear un nuevo usuario.'}
                    </DialogContentText>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        autoFocus={!!selectedUser?.id}
                                        name="name"
                                        label="Nombre Completo"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        value={selectedUser?.name || ''}
                                        onChange={handleUserChange}
                                        required
                                    />
                                </Grid>
                                {selectedUser?.id && (
                                    <Grid item xs={12}>
                                        <TextField
                                            name="password"
                                            label="Nueva Contraseña (opcional)"
                                            type="password"
                                            fullWidth
                                            variant="outlined"
                                            value={selectedUser?.password || ''}
                                            onChange={handleUserChange}
                                            helperText="Dejarlo en blanco para mantener la contraseña actual"
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                name="email"
                                label="Correo Electrónico"
                                type="email"
                                fullWidth
                                variant="outlined"
                                value={selectedUser?.email || ''}
                                onChange={handleUserChange}
                                required
                            />
                        </Grid>
                        {!selectedUser?.id && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    name="password"
                                    label="Contraseña"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={selectedUser?.password || ''}
                                    onChange={handleUserChange}
                                    required
                                />
                            </Grid>
                        )}
                        {Number(selectedUser?.roleId) === 3 && (
                            <>
                                <Typography variant="h6" sx={{ mt: 3, ml: 2 }}>
                                    Datos de la Familia
                                </Typography>
                                <Grid container spacing={2} sx={{ mt: 1, pl: 2 }}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            autoFocus={!selectedUser?.id}
                                            name="familyLastName"
                                            label="Apellido de la Familia"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.familyLastName}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="motherName"
                                            label="Nombre de la Madre"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.motherName}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="motherCellphone"
                                            label="Celular de la Madre"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.motherCellphone}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="motherEmail"
                                            label="Correo de la Madre"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.motherEmail}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="fatherName"
                                            label="Nombre del Padre"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.fatherName}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="fatherCellphone"
                                            label="Celular del Padre"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.fatherCellphone}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="fatherEmail"
                                            label="Correo del Padre"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.fatherEmail}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="razonSocial"
                                            label="Razón Social"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.razonSocial}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="nit"
                                            label="NIT"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.nit}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="mainAddress"
                                            label="Dirección Principal"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.mainAddress}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="alternativeAddress"
                                            label="Dirección Alternativa"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.alternativeAddress}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="zoneOrSector"
                                            label="Zona/Sector"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.zoneOrSector}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="specialFee"
                                            label="Descuento Especial"
                                            type="number"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.specialFee}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl variant="outlined" fullWidth required={!selectedUser?.id}>
                                            <InputLabel>Tipo de Ruta</InputLabel>
                                            <Select
                                                name="routeType"
                                                value={selectedUser?.id ? routeTypeDraft : familyDetail.routeType}
                                                onChange={handleFamilyDetailChange}
                                                onOpen={() => setIsRouteTypeSelectOpen(true)}
                                                onClose={() => setIsRouteTypeSelectOpen(false)}
                                                label="Tipo de Ruta"
                                            >
                                                <MenuItem
                                                    value="Completa"
                                                    onClick={selectedUser?.id ? () => handleRouteTypePick('Completa') : undefined}
                                                >
                                                    Completa
                                                </MenuItem>
                                                <MenuItem
                                                    value="Media AM"
                                                    onClick={selectedUser?.id ? () => handleRouteTypePick('Media AM') : undefined}
                                                >
                                                    Media AM
                                                </MenuItem>
                                                <MenuItem
                                                    value="Media PM"
                                                    onClick={selectedUser?.id ? () => handleRouteTypePick('Media PM') : undefined}
                                                >
                                                    Media PM
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {selectedUser?.id && isRouteTypeSelectOpen && (
                                        <Grid item xs={12} md={4}>
                                            <Paper variant="outlined" sx={(theme) => ({
                                                p: 2,
                                                height: '100%',
                                                borderColor: theme.palette.warning.main,
                                                backgroundColor: alpha(theme.palette.warning.main, 0.10),
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center'
                                            })}>
                                                <Typography variant="body2" sx={(theme) => ({ fontWeight: 700, color: theme.palette.warning.dark })}>
                                                    Cambio de ruta
                                                </Typography>
                                                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                                    - Selecciona un tipo de ruta para proceder con el cambio.
                                                </Typography>
                                                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                                    - Para proceder con el tipo de ruta actual, selecciona el mismo tipo de ruta.
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>

                                {/* Sección de contacto de emergencia */}
                                <Typography variant="h6" sx={{ mt: 3, ml: 2 }}>
                                    Contacto de Emergencia
                                </Typography>
                                <Grid container spacing={2} sx={{ mt: 1, pl: 2 }}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="emergencyContact"
                                            label="Nombre del Contacto"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.emergencyContact}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="emergencyRelationship"
                                            label="Parentesco"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.emergencyRelationship}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            name="emergencyPhone"
                                            label="Teléfono de Emergencia"
                                            fullWidth
                                            variant="outlined"
                                            value={familyDetail.emergencyPhone}
                                            onChange={handleFamilyDetailChange}
                                        />
                                    </Grid>
                                </Grid>
                                
                                {/* Sección de estudiantes */}
                                <Typography variant="h6" sx={{ mt: 3, ml: 2 }}>
                                    Estudiantes
                                </Typography>
                                <Grid container spacing={2} sx={{ mt: 1, pl: 2 }}>
                                    <Grid item xs={12} md={5}>
                                        <TextField
                                            name="fullName"
                                            label="Nombre del Estudiante"
                                            fullWidth
                                            variant="outlined"
                                            value={newStudent.fullName}
                                            onChange={(e) => setNewStudent(prev => ({ ...prev, fullName: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel>Grado</InputLabel>
                                            <Select
                                                name="grade"
                                                label="Grado"
                                                value={newStudent.grade}
                                                onChange={(e) => setNewStudent(prev => ({ ...prev, grade: e.target.value }))}
                                            >
                                                <MenuItem value="">
                                                    <em>Seleccione un grado</em>
                                                </MenuItem>
                                                {availableGrades.length === 0 ? (
                                                    <MenuItem value="">No hay grados disponibles</MenuItem>
                                                ) : (
                                                    availableGrades.map((g, i) => (
                                                        <MenuItem key={i} value={g}>{g}</MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleAddStudent}
                                            fullWidth
                                            sx={{ height: '56px' }}
                                        >
                                            Agregar
                                        </Button>
                                    </Grid>
                                    {Number(selectedUser?.roleId) === 3 && !(Array.isArray(familyDetail.students) && familyDetail.students.some(
                                        s => String(s?.fullName || '').trim() !== '' && String(s?.grade || '').trim() !== ''
                                    )) && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" sx={{ color: '#c62828' }}>
                                                La familia debe tener al menos un estudiante con nombre y grado.
                                            </Typography>
                                        </Grid>
                                    )}
                                    {familyDetail.students.length > 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Estudiantes registrados:
                                            </Typography>
                                            {familyDetail.students.map((student, index) => (
                                                <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
                                                    <Grid item xs={12} md={6}>
                                                        <TextField
                                                            fullWidth
                                                            label={`Nombre del Estudiante ${index + 1}`}
                                                            value={student.fullName || ''}
                                                            onChange={(e) => setFamilyDetail(prev => {
                                                                const copy = Array.isArray(prev.students) ? [...prev.students] : [];
                                                                copy[index] = { ...copy[index], fullName: e.target.value };
                                                                return { ...prev, students: copy };
                                                            })}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={9} md={5}>
                                                        <FormControl fullWidth variant="outlined">
                                                            <InputLabel>{`Grado`}</InputLabel>
                                                            <Select
                                                                value={student.grade || ''}
                                                                label="Grado"
                                                                onChange={(e) => setFamilyDetail(prev => {
                                                                    const copy = Array.isArray(prev.students) ? [...prev.students] : [];
                                                                    copy[index] = { ...copy[index], grade: e.target.value };
                                                                    return { ...prev, students: copy };
                                                                })}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Seleccione un grado</em>
                                                                </MenuItem>
                                                                {availableGrades.length === 0 ? (
                                                                    <MenuItem value="">No hay grados disponibles</MenuItem>
                                                                ) : (
                                                                    availableGrades.map((g, i) => (
                                                                        <MenuItem key={i} value={g}>{g}</MenuItem>
                                                                    ))
                                                                )}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={3} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconButton size="small" onClick={() => setFamilyDetail(prev => ({ ...prev, students: prev.students.filter((_, i) => i !== index) }))}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            </>
                        )}
                        {Number(selectedUser?.roleId) === 6 && (
                            <Box sx={{ mt: 3, clear: 'both', width: '100%' }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    Pilotos a cargo
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Selecciona uno o más pilotos que estarán a cargo de este Supervisor.
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, maxHeight: '200px', overflowY: 'auto' }}>
                                    {!Array.isArray(allPilots) || allPilots.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No hay pilotos disponibles.
                                        </Typography>
                                    ) : (
                                        allPilots.map((pilot) => {
                                            const checked = selectedSupervisorPilots.includes(pilot.id);
                                            return (
                                                <FormControlLabel
                                                    key={pilot.id}
                                                    control={
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={() => handleToggleSupervisorPilot(pilot.id)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={`${pilot.name} - ${pilot.email} (ID: ${pilot.id})`}
                                                    sx={{ display: 'block', mb: 1 }}
                                                />
                                            );
                                        })
                                    )}
                                </Paper>
                            </Box>
                        )}
                        {selectedUser?.roleId === 7 && (
                            <Box sx={{ mt: 3, clear: 'both', width: '100%' }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    Monitoras a cargo
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Selecciona una o más monitoras que estarán a cargo de este Auxiliar.
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, maxHeight: '200px', overflowY: 'auto' }}>
                                    {!Array.isArray(allMonitoras) || allMonitoras.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No hay monitoras disponibles.
                                        </Typography>
                                    ) : (
                                        allMonitoras.map((monitora) => {
                                            const checked = selectedAuxiliarMonitoras.includes(monitora.id);
                                            return (
                                                <FormControlLabel
                                                    key={monitora.id}
                                                    control={
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={() => handleToggleAuxiliarMonitora(monitora.id)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={`${monitora.name} - ${monitora.email} (ID: ${monitora.id})`}
                                                    sx={{ display: 'block', mb: 1 }}
                                                />
                                            );
                                        })
                                    )}
                                </Paper>
                            </Box>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setOpenRouteTypeModal(false);
                        setOpenEditDialog(false);
                    }}>Cancelar</Button>
                    {hasPermission(selectedUser?.id ? 'usuarios-editar' : 'usuarios-crear') && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveUser}
                        disabled={
                            !selectedUser?.id &&
                            Number(selectedUser?.roleId) === 3 &&
                            !(Array.isArray(familyDetail.students) && familyDetail.students.some(
                                s => String(s?.fullName || '').trim() !== '' && String(s?.grade || '').trim() !== ''
                            ))
                        }
                    >
                        {selectedUser?.id ? 'Guardar Cambios' : 'Crear Usuario'}
                    </Button>
                    )}
                </DialogActions>
            </Dialog>

            {selectedUser?.id && (
                <RetroactiveApplyModal
                    open={openRouteTypeModal}
                    onClose={handleCloseRouteTypeModal}
                    mode="ROUTE_TYPE"
                    userId={selectedUser?.id || null}
                    routeType={routeTypeDraft}
                    onApplied={() => {
                        setFamilyDetail(prev => ({ ...prev, routeType: routeTypeDraft }));
                        setOpenRouteTypeModal(false);
                        setSnackbar({ open: true, message: 'Cambio de tipo de ruta aplicado correctamente', severity: 'success' });
                        fetchUsers();
                    }}
                />
            )}
        </PageContainer>
    );
};

export default SchoolUsersPage;
